import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { plannerRequestConfig, PLANNER_AUTO_REFRESH_INTERVAL_MS } from '../lib/plannerRequest';
import { buildDayViewState, SLOT_DURATION } from '../lib/plannerDayData';
import { validateCenterSchedule } from '../lib/plannerSchedule';
import { classifyRequestError } from '../services/api';
import { useRowWindow } from '../hooks/useRowWindow';
import { usePoller } from '../hooks/usePoller';
import {
  buildBookingTemporals,
  buildBookingsByCellKey,
  lookupBookingsForCell,
  buildCompatibleStaffByService,
  buildBlockingBookingsByStaff,
  computeAdminBlockedReason,
  buildVariantLanes,
  buildServiceLaneColumns,
  distributeBookingsIntoColumns,
  resolveLaneHeaderColor,
  laneHasRemainingCapacity,
} from './plannerGrid';
import { format, addDays, startOfWeek, addMinutes, parseISO, isSameDay } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Loader2,
  X,
  Check,
  AlertCircle,
  Search,
  AlertTriangle,
  CalendarCheck,
  Flag,
  Star,
  Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { WeekView } from './WeekView';
import { MonthView } from './MonthView';
import { StaffDayView } from './StaffDayView';

const normalizeForSearch = (s) =>
  (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const normalizePhone = (s) => (s || "").replace(/\D/g, "");

export const normaliseApplyResponse = (payload) => {
  const summary = payload && typeof payload.summary === 'object' && payload.summary !== null
    ? payload.summary
    : {};
  const entries = Array.isArray(payload?.entries)
    ? payload.entries
        .filter((entry) => entry && typeof entry === 'object' && typeof entry.date === 'string')
        .map((entry) => ({
          ...entry,
          date: entry.date,
          time: typeof entry.time === 'string' || typeof entry.time === 'number' ? String(entry.time) : '',
          status: typeof entry.status === 'string' || typeof entry.status === 'number' ? String(entry.status) : '',
          reason: typeof entry.reason === 'string' || typeof entry.reason === 'number' ? String(entry.reason) : '',
        }))
    : [];
  const count = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

  return {
    summary: {
      created: count(summary.created),
      already_existing: count(summary.already_existing),
      failed: count(summary.failed),
    },
    entries,
  };
};

// Row windowing del grid del Planner (D6, R5). Altura de fila fija: el contenido
// de la celda es `absolute` → `min-h-[48px]` es la altura efectiva.
const ROW_H = 48;
// Invariante duro: span máximo de card de la UI (selector [1,2,3,4,6,8] = 8
// slots). Garantiza que la fila de inicio de una card que cruza el borde
// superior del viewport esté montada. Si se permitieran cards de > 8 slots,
// subir en consecuencia.
const OVERSCAN_TOP = 8;
const OVERSCAN_BOTTOM = 2;

const formatClientName = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const [first, ...lastNames] = parts;
  const initials = lastNames
    .map(part => (part[0] || '').toUpperCase())
    .filter(Boolean)
    .map(letter => `${letter}.`)
    .join('');
  return initials ? `${first} ${initials}` : first;
};

export const PlannerVariantLanes = ({
  service,
  bookings,
  bookingsInCell,
  renderBooking,
  onAddClick,
  columns,
  // Section 4 (R4/4.4): when sublanes are active, the parent cell's
  // cell-wide break overlay is suppressed and the break chip is rendered
  // PER LANE. `isBreakForLane` is `(variantId) => boolean` (legacy/null
  // lane passes `null`) and applies the D2 rule for the lane's context.
  // Optional: existing callers / tests that omit it get the legacy
  // per-cell overlay behaviour (no chip per lane).
  isBreakForLane,
  // Section 4 review fix round 1 (R4.5): when provided, clicking the
  // per-lane break chip calls this with the lane's `variantId`. The
  // parent (Planner) wires it to `handleBreakCellClick(time, service.id,
  // variantId)` gated by `!isClient` so the legacy delete affordance is
  // restored when sublanes are active. Omitted → chip stays non-clickable
  // (backwards compatible with existing callers / tests).
  onBreakLaneClick,
  // Section 4 review fix round 1 (R4.5): i18n-resolved label for the
  // per-lane break chip. Parent passes `t('planner.break')` so it matches
  // the parent cell chip's localized label. Defaults to the legacy
  // literal for backwards compatibility.
  breakLabel = 'DESCANSO',
}) => {
  // 6.1 (D7.3): distribute slot bookings into the day-scoped columns when
  // supplied by the parent. Falls back to per-slot derivation for legacy
  // callers / tests that don't pass `columns`.
  const lanes = columns
    ? distributeBookingsIntoColumns(service, bookings, columns)
    : buildVariantLanes(service, bookings);
  if (lanes.length === 0) return null;

  // 6.4 (R5/D6 amended by R8/D9): per-sublane "+" gating. Capacity of any
  // active variant lane is `service.capacityPerSlot`; this is the SAME
  // source of truth as `getCellCapacity` for the parent. We pre-filter the
  // parent cell's bookings to the active set (non-NO_SHOW, non-CANCELLED)
  // so the count matches the parent's aggregated semantics. The bucket
  // is keyed solely by `booking.variantId ?? null` — startDateTime /
  // calendarId are caller-determined (the helper stays pure).
  //
  // The legacy/null lane is NOT a countable "+" target while the feature
  // is ON with active variants (R8.1/R8.2, D9). It remains visible
  // (existing `variantId == null` bookings stay visible — R7.3, D7.5)
  // but never shows a "+" regardless of bucket occupancy. The legacy
  // helper `laneHasRemainingCapacity` is therefore NOT invoked for it.
  const activeBookingsInCell = (bookingsInCell || []).filter(
    (b) => b?.status !== 'NO_SHOW' && b?.status !== 'CANCELLED',
  );

  return (
    <div
      className="absolute inset-0 flex"
      data-testid={`variant-lanes-${service.id}`}
    >
      {lanes.map((lane) => {
        // 2.7 (R5 / QA finding #1): orphan / unknown / historical-unresolvable
        // lanes (i.e. `lane.variant === null` and `lane.variantId !== null`,
        // or `lane.variant.active === false`) do NOT resolve to a countable
        // bucket. They never get a "+" — the legacy/null bucket is the
        // ONLY lane that uses the null target id (R5.1). Variants do not
        // carry a configurable capacity and we never collapse such lanes
        // into legacy/null, never invent metadata for them, and never
        // expose the "+" add-booking control.
        //
        // 6.4 (R8 / D9): the legacy/null lane is no longer a "+" target
        // at all while the feature is ON with active variants. We never
        // call `laneHasRemainingCapacity` for it; visibility stays intact.
        const isLegacyLane = lane.variantId == null;
        const isActiveCatalogLane = lane.variant != null && lane.variant.active !== false;
        // Section 4 (R4/4.4): per-sublane "+" is also gated by the lane's
        // own break state — a per-variant break suppresses only the matching
        // lane's "+", while a per-service or global break (via the same D2
        // rule) suppresses every lane's "+" because they all apply.
        const laneInBreak = isBreakForLane ? isBreakForLane(lane.variantId) : false;
        const canAdd = onAddClick && isActiveCatalogLane && !laneInBreak
          ? laneHasRemainingCapacity(service, lane.variantId, activeBookingsInCell)
          : false;
        return (
          <div
            key={lane.key}
            className="relative flex-1 min-w-0 border-r border-zinc-800/40 last:border-r-0"
            data-testid={`variant-lane-${service.id}-${lane.key}`}
            aria-label={lane.variant?.name || undefined}
          >
            {lane.bookings.map((booking, index) => {
              const renderedBooking = renderBooking(booking, index, lane.bookings.length);
              return React.isValidElement(renderedBooking)
                ? React.cloneElement(renderedBooking, { key: renderedBooking.key || booking.id })
                : renderedBooking;
            })}
            {canAdd && (
              <button
                type="button"
                className="absolute top-1 right-1 z-30 rounded bg-zinc-800/70 px-1.5 py-0.5 text-[10px] text-zinc-200 hover:bg-zinc-700"
                onClick={(e) => {
                  e.stopPropagation();
                  // Resolve to the SAME parent-cell create flow used today;
                  // `variantId` is the only per-lane signal. The parent cell
                  // (HH:mm|calendarId) remains the unique drop target and
                  // backend staff/pin target — backend contracts are not
                  // modified (concerns backend, out of scope here).
                  onAddClick(lane.variantId);
                }}
                title="Añadir reserva"
                data-testid={`variant-lane-add-${service.id}-${lane.key}`}
              >
                +
              </button>
            )}
            {/* Section 4 (R4/4.4): per-lane break chip — only the matching
                lane renders a chip when a per-variant or per-service break
                applies (D2 rule). The parent's cell-wide overlay is
                suppressed in this branch. Per-variant breaks with
                `lane.variantId` not matching render no chip here and the
                parent cell chip is suppressed too, so other variants stay
                visible. Global / per-service breaks still apply (D2(a,b))
                so every lane shows the chip. */}
            {isBreakForLane && isBreakForLane(lane.variantId) && (
              <div
                className={`absolute inset-0 bg-zinc-900/75 z-20 flex items-center justify-center ${onBreakLaneClick ? 'cursor-pointer hover:bg-zinc-800/75' : 'pointer-events-none'}`}
                data-testid={`variant-lane-break-${service.id}-${lane.key}`}
                onClick={onBreakLaneClick ? (e) => { e.stopPropagation(); onBreakLaneClick(lane.variantId); } : undefined}
              >
                <span className="text-[10px] text-zinc-600 uppercase tracking-widest">{breakLabel}</span>
              </div>
            )}
            {/* 6.3 (D7.4): the per-slot variant label is REMOVED. Variants
                are labeled exactly ONCE in the persistent header row; they
                must NOT appear inside individual time slots. */}
          </div>
        );
      })}
    </div>
  );
};


export const Planner = () => {
  const { api, language, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const isClient = user?.role === 'CLIENT';
  // 4.1 (R2.1, R5.3): mismo criterio admin usado en fetchData (~327) pero a
  // nivel de componente, para gatear el checkbox de "pendiente de asignar
  // personal" y la acción de asignación (4.3) sin depender del closure de
  // fetchData.
  const isAdminRole = user?.role === 'CENTER_ADMIN' || user?.role === 'SUPER_ADMIN';

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day');
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState(null);
  
  // Dialog states
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [quickAddSlot, setQuickAddSlot] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    clientId: '',
    calendarId: '',
    startTime: '',
    slotsCount: 2,
    staffId: '',
    variantId: '',
    pendingStaffAssignment: false,
  });
  const [clientSearch, setClientSearch] = useState('');
  const [clientHighlight, setClientHighlight] = useState(-1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [noShowReason, setNoShowReason] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  // 4.3 (R3, R5.2): elección de personal para la acción "Asignar personal"
  // sobre una reserva PENDING — 'auto' = POST sin body (aleatorio);
  // cualquier otro valor = staffId explícito. Estado separado de
  // `selectedStaffId` (que gobierna el flujo de reasignación PUT existente).
  const [assignStaffChoice, setAssignStaffChoice] = useState('auto');
  const [internalNote, setInternalNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  // 6.1 (D10/D12): operación de creación de booking puede terminar en
  // UNKNOWN si el POST /api/bookings tarda >20 s o sufre abort. Conservamos
  // el estado del modal y la key para que el retry explícito re-envíe la
  // misma idempotencyKey (D12). No persistimos la key en sessionStorage.
  const [bookingUnknown, setBookingUnknown] = useState(false);
  const [bookingUnknownMessage, setBookingUnknownMessage] = useState('');
  // Round-2 fix for review panel finding (UI-UX reconciling indicator):
  // when UNKNOWN is surfaced, the background reconciliation GET runs for
  // up to 20 s. Without a visible indicator the user may click Retry
  // or Cancel during the silent check, producing a double-resolution.
  // We toggle this flag in lockstep with the recon GET so the
  // `booking-create-unknown` block can render a `Reconciling…` sub-label
  // for as long as the GET is in flight.
  const [bookingReconciling, setBookingReconciling] = useState(false);
  const createBookingKeyRef = useRef(null);
  // 6.1 §3.1 (R1.3): snapshot of the exact POST body sent on the first
  // attempt of a logical create-booking operation. Frozen in lockstep with
  // `createBookingKeyRef` so a Reintentar in UNKNOWN resends the identical
  // payload even if `selectedDate`/form fields changed underneath it.
  const createBookingPayloadRef = useRef(null);
  const createBookingRequestRef = useRef(null);

  // Drag state
  const [draggedBooking, setDraggedBooking] = useState(null);

  // CLIENT availability map: { [calendarId]: { [HH:mm]: occupied } }
  const [availabilityMap, setAvailabilityMap] = useState({});

  // Breaks + template apply state
  const [breaks, setBreaks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showBreakDialog, setShowBreakDialog] = useState(false);
  const [selectedBreak, setSelectedBreak] = useState(null);
  // Section 4 (R4/4.4): creation dialog state. Pre-filled from the cell
  // that opened the dialog (quickAddSlot/selectedSlot). The user may edit
  // `calendarId` and `variantId` here before POSTing to /breaks.
  const [showBreakCreateDialog, setShowBreakCreateDialog] = useState(false);
  const [breakCreateForm, setBreakCreateForm] = useState({
    date: '',
    startTime: '',
    endTime: '',
    calendarId: '',
    variantId: '',
  });
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applyingService, setApplyingService] = useState(null); // { id, name }
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [applyStartDate, setApplyStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [applyEndDate, setApplyEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [applyValidationError, setApplyValidationError] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const applyPayloadRef = useRef(null);

  // Export day as template
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportTemplateName, setExportTemplateName] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  const isFetchingRef = useRef(false);

  // Estado de error recuperable (D3/R2): { kind, retry } cuando el fetch falla
  // con un modo transitorio. Banner no bloqueante con reintento explícito.
  const [plannerError, setPlannerError] = useState(null);

  // Guard de secuencia (D1/R1): contador monotónico. Cada arranque de fetchData
  // captura su token; solo la última selección confirmada aplica su resultado.
  const selectionTokenRef = useRef(0);
  // AbortController de la invocación en curso, para cancelar de verdad la
  // request de una selección abandonada (no solo ignorar su resultado).
  const abortRef = useRef(null);
  // Puntero al fetchData vigente, para que `plannerError.retry` re-ejecute la
  // versión con las dependencias actuales sin capturar un closure obsoleto.
  const fetchDataRef = useRef(null);

  // Auto-refresco del Day Planner (§3, R1-R4). Refs propios del refresco en
  // segundo plano — NO comparten AbortController ni token de selección con
  // fetchData (D2).
  //  - autoRefreshAbortRef: AbortController del tick en vuelo (abortado al
  //    desmontar y, en §4, por fetchData).
  //  - autoRefreshAuthFailedRef: 401/403 en un tick → dejar de refrescar hasta
  //    el próximo fetchData exitoso (D5/R2.3; §4 lo resetea).
  //  - lastDaySignatureRef: firma JSON del último payload aplicado; si no
  //    cambia, no se hace setState (D6/R2.4).
  //  - dropInFlightRef: PUT de drag&drop en vuelo (§4 lo cablea en handleDrop);
  //    aquí solo debe existir y ser leído por la puerta.
  //  - mountedRef: para re-validar montaje tras el await, antes de setState.
  const autoRefreshAbortRef = useRef(null);
  const autoRefreshAuthFailedRef = useRef(false);
  const lastDaySignatureRef = useRef(null);
  const dropInFlightRef = useRef(false);
  const mountedRef = useRef(true);

  // Fetch data
  const fetchData = useCallback(async () => {
    // Guard de secuencia: incrementa el token en cada arranque (D1).
    const myToken = ++selectionTokenRef.current;
    // Cancela la request de la selección anterior aún en vuelo.
    if (abortRef.current) abortRef.current.abort();
    // §4/D2: aborta también el refresco automático en vuelo (cambio de fecha /
    // nuevo arranque de fetchData ⇒ token++ ⇒ el tick queda obsoleto).
    if (autoRefreshAbortRef.current) autoRefreshAbortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);

      const dateStr = format(selectedDate, 'yyyy-MM-dd');


      const isClient = user?.role === 'CLIENT';
      const isCenterAdminRole = user?.role === 'CENTER_ADMIN';
      const isAdminRole = user?.role === 'CENTER_ADMIN' || user?.role === 'SUPER_ADMIN';

      const cfg = plannerRequestConfig({ signal: controller.signal });
      const requests = [
        api.get('/calendars', cfg),                                               // [0]
        api.get(`/availability?date=${dateStr}`, cfg),                            // [1] always
        api.get(`/bookings?date=${dateStr}`, cfg),                                // [2]
        api.get('/clients', cfg),                                                  // [3]
        ...(isAdminRole ? [api.get('/users?role=STAFF', cfg)] : []),             // [4] admin only
        api.get('/centers', cfg),                                                  // [4] or [5]
        api.get(`/breaks?date=${dateStr}`, cfg),                                  // [5] or [6]
        ...(isCenterAdminRole ? [api.get('/weekly-templates', cfg)] : []),       // [6] or [7]
      ];

      const results = await Promise.all(requests);

      // Descarta la respuesta si ya hay una selección posterior (llegada fuera
      // de orden o simultánea de una selección abandonada) (D1/R1).
      if (myToken !== selectionTokenRef.current) {
        console.debug('[planner] stale response dropped', { token: myToken });
        return false;
      }

      const calendarsRes = results[0];
      const availabilityRes = results[1];
      const bookingsRes = results[2];
      const clientsRes = results[3];
      const staffRes = isAdminRole ? results[4] : null;
      const centersRes = isAdminRole ? results[5] : results[4];
      const breaksRes = isAdminRole ? results[6] : results[5];
      const templatesRes = isCenterAdminRole ? results[7] : null;
setServices(calendarsRes.data.filter(s => s.active));

      const { availabilityMap, bookings, breaks } = buildDayViewState({
        availabilityRes,
        bookingsRes,
        breaksRes,
        isClient,
        t,
      });
      setAvailabilityMap(availabilityMap);
      setBookings(bookings);
      setBreaks(breaks);
      // §4/R2.4: mantén la firma aplicada en sync con el estado recién cargado
      // (MISMA firma/orden que el tick: availability, bookings, breaks) para que
      // el primer tick tras una carga completa no re-aplique datos idénticos.
      lastDaySignatureRef.current = JSON.stringify([availabilityRes.data, bookingsRes.data, breaksRes.data]);
      // §4/R2.3: un fetchData exitoso reanuda el refresco automático tras 401/403.
      autoRefreshAuthFailedRef.current = false;

      setClients(clientsRes.data.filter(c => c.active));
      setStaffUsers(staffRes?.data || []);
      if (centersRes.data.length > 0) {
        // R5.1/D9: el centro debe traer un horario VALIDADO antes de llegar
        // al cálculo de slots (y a WeekView/MonthView/StaffDayView, que
        // parsean el mismo campo). Un horario ausente, no textual o malformado
        // NO se sustituye por un horario inventado: se clasifica como error
        // recuperable con reintento explícito.
        const nextCenter = centersRes.data[0];
        const schedule = validateCenterSchedule(nextCenter);
        if (!schedule.valid) {
          console.warn('[planner] invalid center schedule → recoverable', {
            reason: schedule.reason,
          });
          setCenter(null);
          setPlannerError({
            kind: 'invalid-schedule',
            reason: schedule.reason,
            retry: () => fetchDataRef.current && fetchDataRef.current(),
          });
          return false;
        }
        setCenter(nextCenter);
      }
      if (templatesRes) setTemplates(templatesRes.data || []);
      // Éxito: limpia cualquier error recuperable pendiente (p.ej. tras retry).
      setPlannerError(null);
    } catch (error) {
      const kind = classifyRequestError(error);
      // Cancelación intencional por cambio de selección (D1/D3): neutral. No
      // banner, no toast, no retry, no cambio de estado visible.
      if (kind === 'canceled') return false;
      // Error de una selección ya superada: descarta sin tocar el estado vigente.
      if (myToken !== selectionTokenRef.current) return false;
      if (kind === 'transient') {
        // R2: estado recuperable. NO se limpian los `bookings` ya cargados.
        console.warn('[planner] request timeout → recoverable', { kind });
        setPlannerError({ kind, retry: () => fetchDataRef.current && fetchDataRef.current() });
      } else {
        // permanent/auth: conserva la semántica actual (toast + flujo existente).
        // R1.2/R5.2: NO se registra el objeto de error de axios: su
        // `config.headers.Authorization` es el JWT y `response.data` son datos
        // personales. Sólo estado y código de motivo.
        console.error('[planner] fetch data failed', {
          status: error?.response?.status ?? 'network',
          code: error?.code || 'unknown',
        });
        toast.error(t('planner.toast.loadError'));
      }
      return false;
    } finally {
      // Solo la última selección controla el loading (una invocación abortada
      // no debe apagar el spinner de la selección vigente).
      if (myToken === selectionTokenRef.current) setLoading(false);
    }
    return true;
  }, [api, selectedDate, user?.role, t]);

  // Mantiene `fetchDataRef` apuntando al fetchData vigente para el retry.
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // 6.1 (D10): al desmontar Planner, aborta cualquier POST /api/bookings
  // pendiente. Cancelar la espera del frontend NO cancela el procesamiento
  // backend; el estado UNKNOWN + reconciliación por key son los que
  // protegen frente a doble POST ciego.
  useEffect(() => () => {
    const request = createBookingRequestRef.current;
    createBookingRequestRef.current = null;
    request?.controller.abort();
  }, []);

  // 6.1 (D12): al cerrar el modal de creación (cancel, ESC, backdrop),
  // limpiamos el estado UNKNOWN y descartamos la key — ya no es la misma
  // operación lógica. El siguiente create generará key nueva.
  useEffect(() => {
    if (!showBookingDialog) {
      setBookingUnknown(false);
      setBookingUnknownMessage('');
      createBookingKeyRef.current = null;
      createBookingPayloadRef.current = null;
    }
  }, [showBookingDialog]);

  const submitApply = useCallback(async (request) => {
    applyPayloadRef.current = request;
    setApplyLoading(true);
    setApplyError(null);
    try {
      const res = await api.post(
        `/weekly-templates/${request.templateId}/apply`,
        request.payload
      );
      setApplyResult(normaliseApplyResponse(res.data));
      const resultSummary = res.data?.summary;
      const malformedSummary = resultSummary && typeof resultSummary === 'object' &&
        ['created', 'already_existing', 'failed'].some((key) =>
          resultSummary[key] != null && !Number.isFinite(Number(resultSummary[key]))
        );
      const malformedEntries = Array.isArray(res.data?.entries) && res.data.entries.some((entry) =>
        !entry || typeof entry !== 'object' || typeof entry.date !== 'string'
      );
      if (!res.data || typeof res.data !== 'object' || malformedSummary || malformedEntries) {
        setApplyError('El resultado de aplicar la plantilla no tiene un formato válido.');
      }
      const refreshed = await fetchData();
      if (!refreshed) {
        setPlannerError((previous) => previous || {
          kind: 'transient',
          retry: () => fetchDataRef.current && fetchDataRef.current(),
        });
      }
    } catch (err) {
      setApplyError(
        err?.response?.data?.detail || err?.message || t('planner.toast.applyTemplateError')
      );
    } finally {
      setApplyLoading(false);
    }
  }, [api, fetchData, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch when the user returns to this tab (catches cancellations made elsewhere)
  // isFetchingRef guards against overlapping requests on rapid tab switches
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      fetchData().finally(() => { isFetchingRef.current = false; });
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchData]);

  // §3.1 (D4): busyRef se reasigna en CADA render (patrón fnRef de usePoller)
  // exponiendo los flags de la puerta derivados de estado. Así
  // getAutoRefreshSkipReason lee SIEMPRE los valores más recientes — también
  // DESPUÉS del await de un tick en vuelo — sin re-armar el poller en cada
  // render (identidad estable de la puerta).
  const busyRef = useRef({});
  busyRef.current = {
    loading,
    isFetching: isFetchingRef.current,
    showBookingDialog,
    showStatusDialog,
    showBreakDialog,
    showApplyDialog,
    showExportDialog,
    quickAddSlot,
    formLoading,
    noteLoading,
    applyLoading,
    exportLoading,
    bookingUnknown,
    bookingReconciling,
    draggedBooking,
  };

  // §3.2 (D4): puerta única del auto-refresh. Devuelve el PRIMER motivo
  // aplicable o null. Se evalúa dos veces por tick: al entrar (puerta completa)
  // y tras el await (con `ignoreHidden` — que la pestaña se oculte durante la
  // request no invalida un payload ya recibido). Lee los flags de estado vía
  // busyRef y los refs directamente (siempre vigentes).
  const getAutoRefreshSkipReason = useCallback((options = {}) => {
    const { ignoreHidden = false } = options;
    const b = busyRef.current;
    if (!ignoreHidden && document.visibilityState !== 'visible') return 'hidden';
    if (b.loading || b.isFetching) return 'loading';
    if (b.showBookingDialog || b.showStatusDialog || b.showBreakDialog ||
        b.showApplyDialog || b.showExportDialog || b.quickAddSlot) return 'dialog';
    if (b.formLoading || b.noteLoading || b.applyLoading || b.exportLoading ||
        b.bookingUnknown || b.bookingReconciling || b.draggedBooking ||
        dropInFlightRef.current) return 'mutation';
    if (autoRefreshAuthFailedRef.current) return 'auth-failed';
    return null;
  }, []);

  // §3.3 (D2/D5/D6): tick del refresco automático. NO incrementa
  // selectionTokenRef — el refresco NO es dueño del token de selección, solo lo
  // lee para descartar respuestas obsoletas. Pide EXACTAMENTE /availability,
  // /bookings y /breaks de la fecha visible (R1.2) con su propio
  // AbortController. Nunca toca `loading`, `plannerError` ni muestra toast.
  const autoRefreshTick = useCallback(async () => {
    const skip = getAutoRefreshSkipReason();
    if (skip != null) {
      console.debug('[planner] auto-refresh skipped', { reason: skip });
      return;
    }
    // Lee el token SIN incrementarlo (D2).
    const myToken = selectionTokenRef.current;
    const controller = new AbortController();
    autoRefreshAbortRef.current = controller;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const cfg = plannerRequestConfig({ signal: controller.signal });
    try {
      const [availabilityRes, bookingsRes, breaksRes] = await Promise.all([
        api.get(`/availability?date=${dateStr}`, cfg),
        api.get(`/bookings?date=${dateStr}`, cfg),
        api.get(`/breaks?date=${dateStr}`, cfg),
      ]);
      // Re-validación tras el await, ANTES de cualquier setState (D2/R3.1):
      // seguir montado, token intacto (no cambió la fecha ni arrancó fetchData)
      // y puerta (sin `hidden`) aún libre. Cierra la carrera de escritura tardía.
      if (!mountedRef.current ||
          myToken !== selectionTokenRef.current ||
          getAutoRefreshSkipReason({ ignoreHidden: true }) != null) {
        console.debug('[planner] auto-refresh dropped (stale)');
        return;
      }
      // §3.3 (D6/R2.4): firma del payload. Si es idéntica, no hay setState → no
      // re-render → scroll intacto.
      const signature = JSON.stringify([availabilityRes.data, bookingsRes.data, breaksRes.data]);
      if (signature === lastDaySignatureRef.current) {
        return;
      }
      const { availabilityMap, bookings, breaks } = buildDayViewState({
        availabilityRes,
        bookingsRes,
        breaksRes,
        isClient,
        t,
      });
      setAvailabilityMap(availabilityMap);
      setBookings(bookings);
      setBreaks(breaks);
      lastDaySignatureRef.current = signature;
    } catch (error) {
      // §3.4 (D5/R2.2): nunca plannerError/toast/loading.
      const kind = classifyRequestError(error);
      if (kind === 'canceled') return;
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        // 401/403 → dejar de refrescar hasta el próximo fetchData exitoso (§4).
        autoRefreshAuthFailedRef.current = true;
        return;
      }
      console.debug('[planner] auto-refresh failed', { kind });
    }
  }, [api, selectedDate, isClient, t, getAutoRefreshSkipReason]);

  // §3.5 (R1.1/R1.4/R4.3): refresco automático mientras el Day Planner está en
  // vista `day` y la pestaña visible (la puerta cubre visibilidad). immediate:
  // false → el primer tick ocurre a los 30s, sin duplicar el fetch de montaje
  // que ya hace fetchData. usePoller garantiza ≤1 en vuelo.
  usePoller(autoRefreshTick, PLANNER_AUTO_REFRESH_INTERVAL_MS, viewMode === 'day', { immediate: false });

  // §3.5 (R4.4): al desmontar, detén el poller (su propio cleanup) y aborta
  // cualquier refresco en vuelo para que no haga setState después.
  useEffect(() => () => {
    mountedRef.current = false;
    if (autoRefreshAbortRef.current) autoRefreshAbortRef.current.abort();
  }, []);

  // Generate time slots
  // R5.1/D9: el horario se toma de la validación pura; sin centro o con un
  // horario inválido no se calcula ningún slot (y nunca se inventa '08:00').
  const timeSlots = useMemo(() => {
    const schedule = validateCenterSchedule(center);
    if (!schedule.valid) return [];

    const slots = [];
    const openHour = schedule.openingTime.hour;
    const openMin = schedule.openingTime.minute;
    const closeHour = schedule.closingTime.hour;
    const closeMin = schedule.closingTime.minute;

    let current = new Date(selectedDate);
    current.setHours(openHour, openMin, 0, 0);

    const end = new Date(selectedDate);
    end.setHours(closeHour, closeMin, 0, 0);

    while (current < end) {
      slots.push(new Date(current));
      current = addMinutes(current, SLOT_DURATION);
    }

    return slots;
  }, [center, selectedDate]);

  // Row windowing del grid del día (D6, R5): solo se montan las filas visibles
  // (+ overscan); los spacers preservan el `scrollHeight` completo (DnD/auto-scroll
  // nativos intactos). `scrollRef` apunta al contenedor `.planner-scroll`.
  const scrollRef = useRef(null);
  const rowWindow = useRowWindow(scrollRef, ROW_H, timeSlots.length, OVERSCAN_TOP, OVERSCAN_BOTTOM);

  // Get bookings for a specific cell
  const bookingsByCalendar = useMemo(() => {
    const map = {};
    for (const booking of bookings) {
      if (!map[booking.calendarId]) map[booking.calendarId] = [];
      map[booking.calendarId].push(booking);
    }
    return map;
  }, [bookings]);

  // Shared per-booking temporal precompute (D6, R5): one parseISO per booking
  // per bookings-change, reused by both grid indices and the booking-node render
  // (numeric ms + HH:mm labels). See plannerGrid.js / planner_grid_bookingTemporals.test.js.
  const bookingTemporals = useMemo(
    () => buildBookingTemporals(bookings, SLOT_DURATION),
    [bookings]
  );

  // Precomputed "HH:mm|calendarId" -> bookings[] index (D6, R5): built once per
  // bookings/slots change so each cell is an O(1) lookup instead of a per-cell
  // filter over the whole calendar. Semantics preserved bit-for-bit (see
  // plannerGrid.js / planner_grid_bookingsByCellKey.test.js).
  const bookingsByCellKey = useMemo(
    () => buildBookingsByCellKey(bookingsByCalendar, timeSlots, bookingTemporals, SLOT_DURATION),
    [bookingsByCalendar, timeSlots, bookingTemporals]
  );

  const getBookingsForCell = useCallback(
    (time, calendarId) => lookupBookingsForCell(bookingsByCellKey, time, calendarId),
    [bookingsByCellKey]
  );

  // 6.1 (D7.3, D7.5) — service lane columns are computed ONCE per service and
  // day, not per slot. The header emits them exactly once and every time row
  // aligns underneath. `dayBookings` is the per-service booking list filtered
  // to the planner's selectedDate (today's local day). The columns do NOT
  // shift between time rows: a column either exists for the whole day or it
  // does not exist at all.
  const serviceLaneColumns = useMemo(() => {
    const map = {};
    const dayKey = format(selectedDate, 'yyyy-MM-dd');
    for (const service of services) {
      const all = bookingsByCalendar[service.id] || [];
      const dayBookings = all.filter(b => {
        try {
          return format(parseISO(b.startDateTime), 'yyyy-MM-dd') === dayKey;
        } catch (e) {
          return false;
        }
      });
      map[service.id] = buildServiceLaneColumns(service, dayBookings);
    }
    return map;
  }, [services, bookingsByCalendar, selectedDate]);

  // 6.2 / 6.4 (R6, D7.1, D9) — does the persistent-column layout apply?
  // Header emits a second row only when at least one service has variant
  // columns; per-slot sublanes are still emitted by PlannerVariantLanes only
  // when those columns actually exist. Active catalog variants are
  // always present in the day columns (buildServiceLaneColumns), so the
  // boolean reduces to "any service has the active variant flag on".
  const hasAnyLaneColumns = useMemo(
    () => services.some(s => (serviceLaneColumns[s.id] || []).length > 0),
    [services, serviceLaneColumns]
  );

  // Calculate capacity for a cell
  const getCellCapacity = useCallback((time, calendarId) => {
    const service = services.find(s => s.id === calendarId);
    if (!service) return { used: 0, total: 0 };

    // For CLIENT: use server-side occupancy data so the count is accurate
    // (placeholder bookings are merged objects — counting them gives wrong results)
    if (availabilityMap[calendarId]) {
      const hhmm = format(time, 'HH:mm');
      const occupied = availabilityMap[calendarId][hhmm] ?? 0;
      return { used: occupied, total: service.capacityPerSlot };
    }

    const cellBookings = getBookingsForCell(time, calendarId);
    const activeBookings = cellBookings.filter(b => b.status !== 'NO_SHOW' && b.status !== 'CANCELLED');

    return {
      used: activeBookings.length,
      total: service.capacityPerSlot,
    };
  }, [services, getBookingsForCell, availabilityMap]);

  // Break detection — string comparison works correctly for zero-padded HH:MM.
  // Section 4 (R2/D4): per-service/per-variant filtering applies the D2 rule
  // in the client: a break with `b.calendarId == null` always applies (legacy
  // global overlay); when a `calendarId` context is supplied, a break applies
  // iff `b.calendarId === calendarId` AND (`b.variantId == null` OR
  // `b.variantId === variantId`). Call sites that only know `time` still work
  // through the defaults (`calendarId=null, variantId=null`), which keeps the
  // legacy global-only behaviour for legacy code paths.
  const isBreakTime = useCallback((time, calendarId = null, variantId = null) => {
    const hhmm = format(time, 'HH:mm');
    return breaks.some((b) => {
      if (hhmm < b.startTime || hhmm >= b.endTime) return false;
      if (b.calendarId == null) return true; // (a) global legacy break
      if (calendarId == null) return false; // (b) per-service break does not apply without a service context
      if (b.calendarId !== calendarId) return false;
      if (b.variantId == null) return true; // (b) per-service, all variants
      return b.variantId === variantId; // (c) per-variant
    });
  }, [breaks]);

  const staffById = useMemo(() => {
    return Object.fromEntries(staffUsers.map(staff => [staff.id, staff]));
  }, [staffUsers]);

  const staffForView = useMemo(() => {
    if (user?.role === 'STAFF') return [{ id: user.id, fullName: user.fullName }];
    return staffUsers;
  }, [user, staffUsers]);

  // Admin-side "no staff available" check — computed from already-loaded
  // bookings + staffUsers. D6 (R5): the cell-independent filters are precomputed
  // into memoized indices so each cell scans only the compatible staff's own
  // bookings (no full-array scan, no parseISO in the hot loop). Semantics
  // preserved bit-for-bit — see plannerGrid.js / planner_grid_adminBlockedReason.test.js.
  const compatibleStaffByService = useMemo(
    () => buildCompatibleStaffByService(staffUsers, services),
    [staffUsers, services]
  );
  const blockingBookingsByStaff = useMemo(
    () => buildBlockingBookingsByStaff(bookings, bookingTemporals),
    [bookings, bookingTemporals]
  );
  const getAdminBlockedReason = useCallback((time, calendarId) => {
    if (isClient || staffUsers.length === 0) return null;
    const service = services.find(s => s.id === calendarId);
    if (!service) return null;
    return computeAdminBlockedReason({
      compatibleStaffByService,
      blockingBookingsByStaff,
      service,
      time,
      calendarId,
      slotDuration: SLOT_DURATION,
    });
  }, [isClient, staffUsers, services, compatibleStaffByService, blockingBookingsByStaff]);


  // Daily attendance counters per service (CENTER_ADMIN only, computed from loaded bookings)
  // R5.1/D9: el rango se toma de la validación pura; sin centro válido no hay
  // rango (y no se inventa '08:00'/'21:00').
  const dailyServiceStats = useMemo(() => {
    const schedule = validateCenterSchedule(center);
    if (!schedule.valid) return {};
    const openH = schedule.openingTime.hour;
    const openM = schedule.openingTime.minute;
    const closeH = schedule.closingTime.hour;
    const closeM = schedule.closingTime.minute;
    const map = {};
    for (const b of bookings) {
      if (!b.calendarId) continue;
      const d = new Date(b.startDateTime);
      const h = d.getHours(), m = d.getMinutes();
      if ((h > openH || (h === openH && m >= openM)) === false) continue;
      if ((h < closeH || (h === closeH && m < closeM)) === false) continue;
      if (!map[b.calendarId]) map[b.calendarId] = { attended: 0, reserved: 0 };
      if (b.status === 'ATTENDED') map[b.calendarId].attended += 1;
      else if (b.status === 'RESERVED') map[b.calendarId].reserved += 1;
    }
    return map;
  }, [bookings, center]);

  // Navigation handlers
  const goToToday = () => setSelectedDate(new Date());
  const goToPrevious = () => setSelectedDate(d => addDays(d, viewMode === 'week' ? -7 : -1));
  const goToNext = () => setSelectedDate(d => addDays(d, viewMode === 'week' ? 7 : 1));

  // Section 4 (R4/4.3): carry the cell's (calendarId, variantId) into
  // `quickAddSlot` so that `handleQuickAddBreak` can later POST the same
  // targeting to `/breaks`. Without `variantId` here, the break creation
  // path would silently drop back to the legacy global-only behaviour even
  // when the user clicked a per-variant sublane "+".
  const handleQuickAddClick = (time, calendarId, variantId = null) => {
    setQuickAddSlot({ time, calendarId, variantId });
    handleCellClick(time, calendarId, variantId);
  };

  // Section 4 (R2/D4): open the dialog for the FIRST break that applies to
  // the cell's (calendarId, variantId) — the same D2 rule as `isBreakTime`.
  // When the cell context is provided (line 1692 call site) the dialog
  // resolves to the per-service/per-variant break that actually painted the
  // chip, never a coincidentally overlapping global break. Legacy call sites
  // still pass only `time` and get the legacy global-only behaviour.
  const handleBreakCellClick = (time, calendarId = null, variantId = null) => {
    const hhmm = format(time, 'HH:mm');
    const matchesTime = (b) => hhmm >= b.startTime && hhmm < b.endTime;
    const appliesTo = (b) => {
      if (b.calendarId == null) return true;
      if (calendarId == null) return false;
      if (b.calendarId !== calendarId) return false;
      if (b.variantId == null) return true;
      return b.variantId === variantId;
    };
    const brk = breaks.find((b) => matchesTime(b) && appliesTo(b));
    if (brk) {
      setSelectedBreak(brk);
      setShowBreakDialog(true);
    }
  };

  const handleDeleteBreak = async () => {
    if (!selectedBreak) return;
    setFormLoading(true);
    try {
      await api.delete(`/breaks/${selectedBreak.id}`);
      toast.success(t('success.planner.breakDeleted'));
      setShowBreakDialog(false);
      setSelectedBreak(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar descanso');
    } finally {
      setFormLoading(false);
    }
  };

  // Section 4 (R4/4.3 + 4.4): opens the break creation dialog. The dialog
  // pre-selects the cell's (calendarId, variantId) from the originating slot
  // (either `quickAddSlot` — set by the per-sublane "+" — or `selectedSlot`
  // — set by the parent cell click that opened the booking dialog). The
  // actual POST happens in `handleCreateBreakSubmit` once the user confirms.
  const handleQuickAddBreak = () => {
    const slot = quickAddSlot || selectedSlot;
    if (!slot) return;
    setBreakCreateForm({
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: format(slot.time, 'HH:mm'),
      endTime: format(addMinutes(slot.time, SLOT_DURATION), 'HH:mm'),
      calendarId: slot.calendarId || '',
      variantId: slot.variantId || '',
    });
    setShowBreakCreateDialog(true);
    setShowBookingDialog(false);
  };

  // Section 4 (R4/4.3): the actual POST. Includes `calendarId` (when the
  // dialog picked a service) and `variantId` (only when the chosen service
  // has `enablePlanningVariants === true` AND the dialog selected a variant).
  // If the chosen service has the feature OFF, `variantId` is NOT sent so
  // the backend validation `break_variant_disabled_for_calendar` cannot fire.
  const handleCreateBreakSubmit = async () => {
    const f = breakCreateForm;
    if (!f.calendarId) {
      toast.error('Selecciona un servicio para el descanso');
      return;
    }
    const service = services.find((s) => s.id === f.calendarId);
    const featureOn = service?.enablePlanningVariants === true;
    const activeOnly = ((service?.variants) || []).filter((v) => v.active !== false);
    const payload = {
      date: f.date,
      startTime: f.startTime,
      endTime: f.endTime,
      calendarId: f.calendarId || null,
    };
    if (featureOn && activeOnly.length >= 2 && f.variantId) {
      payload.variantId = f.variantId;
    }
    const variantLabel = payload.variantId
      ? ` (${(service?.variants || []).find((v) => v.id === f.variantId)?.name || f.variantId})`
      : '';
    setFormLoading(true);
    try {
      await api.post('/breaks', payload);
      toast.success(
        payload.calendarId
          ? `Descanso creado para ${service?.name || ''}${variantLabel}`
          : 'Descanso creado'
      );
      setShowBreakCreateDialog(false);
      setBreakCreateForm({ date: '', startTime: '', endTime: '', calendarId: '', variantId: '' });
      setQuickAddSlot(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear descanso');
    } finally {
      setFormLoading(false);
    }
  };

  const handleNewBookingForStaff = useCallback((staffId) => {
    setFormData({ clientId: '', calendarId: '', startTime: '', slotsCount: 2, staffId, pendingStaffAssignment: false });
    setClientSearch('');
    setClientHighlight(-1);
    setShowBookingDialog(true);
  }, []);

  // Open booking dialog
  const handleCellClick = (time, calendarId, variantId = null) => {
    const service = services.find(s => s.id === calendarId);
    // For CLIENT: auto-select their own client record (backend returns only theirs)
    const selfClientId = isClient
      ? (clients.find(c => c.userId === user?.id)?.id || clients[0]?.id || '')
      : '';
    // Section 4 (R4/4.3): carry variantId into selectedSlot so the
    // `handleQuickAddBreak` path can read it from `selectedSlot` when the
    // user clicks "Crear descanso en su lugar" inside the booking dialog.
    setSelectedSlot({ time, calendarId, variantId });
    setFormData({
      clientId: selfClientId,
      calendarId: calendarId,
      startTime: format(time, 'HH:mm'),
      slotsCount: service?.slotsPerBooking || 2,
      staffId: '',
      variantId: variantId || '',
      pendingStaffAssignment: false,
    });
    setClientSearch('');
    setShowBookingDialog(true);
  };

  // Open booking details
  const handleBookingClick = (booking, e) => {
    e.stopPropagation();
    if (booking?.isPlaceholder) return;
    setSelectedBooking(booking);
    setNoShowReason(booking.noShowReason || '');
    setSelectedStaffId(booking.staffId || '');
    setInternalNote(booking.internalNote || '');
    setAssignStaffChoice('auto');
    setShowStatusDialog(true);
  };

  // 6.7 (R8, D9): a variant is required when the active calendar has the
  // feature ON with at least one active variant. When the feature is OFF
  // (or no active variants exist), this returns `true` so the legacy
  // flow stays bit-identical.
  const isCreateVariantValid = useCallback(() => {
    const activeService = services.find(s => s.id === formData.calendarId);
    const featureOn = activeService?.enablePlanningVariants === true;
    const activeOnly = ((activeService?.variants) || []).filter(v => v.active !== false);
    if (!featureOn || activeOnly.length === 0) return true;
    return !!formData.variantId;
  }, [services, formData.calendarId, formData.variantId]);

  // Create booking
  const handleCreateBooking = async () => {
    // 6.7 (R8, D9): when the active calendar has the feature ON AND at
    // least one active variant, a valid `variantId` is mandatory. The
    // dialog button is disabled via `isCreateVariantValid`; this guard
    // is a defense-in-depth check in case the user bypasses the button.
    if (!formData.clientId || !formData.calendarId) {
      toast.error(t('planner.selectClient'));
      return;
    }
    if (!isCreateVariantValid()) {
      toast.error(t('success.planner.selectVariantRequired'));
      return;
    }

    // 6.1 (D12): generamos UNA key por operación lógica de creación. Se
    // conserva en ref para que el retry explícito re-envíe la MISMA key y
    // el backend pueda reconciliar (replay COMPLETED, 409 PROCESSING, etc.).
    // No se persiste en sessionStorage/localStorage (security §3): vive
    // solo en el ref mientras la operación está en vuelo.
    let idempotencyKey = createBookingKeyRef.current;
    let requestBody = createBookingPayloadRef.current;
    if (!idempotencyKey) {
      const uuid = (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null);
      idempotencyKey = uuid || `bk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      createBookingKeyRef.current = idempotencyKey;

      // 6.1 §3.1 (R1.3, D2): freeze the exact POST body on the FIRST
      // attempt only. A Reintentar in UNKNOWN reuses this stored object
      // verbatim, even if `selectedDate`/form fields drift underneath it
      // while the dialog is disabled.
      const startDateTime = new Date(selectedDate);
      const [hours, minutes] = formData.startTime.split(':').map(Number);
      startDateTime.setHours(hours, minutes, 0, 0);

      // OWN_ONLY STAFF must send an explicit owner even when creation starts
      // from a normal Planner cell (that flow intentionally leaves
      // formData.staffId empty). Admin roles and ALL keep the existing payload
      // unchanged. Se resuelve DENTRO del freeze (D2) para que el owner viaje
      // en el payload congelado y un Reintentar en UNKNOWN reenvíe la misma
      // elección (merge planner-staff-visibility-controls, PR #28).
      const staffIdForCreate = user?.role === 'STAFF' && center?.staffPlannerVisibility === 'OWN_ONLY'
        ? user.id
        : formData.staffId;

      requestBody = {
        clientId: formData.clientId,
        calendarId: formData.calendarId,
        // Backend deriva centerId del JWT (steering/security.md §1).
        // Lo retiramos del body para honrar la regla "tenant del JWT,
        // nunca del cliente". Caveat: SUPER_ADMIN sin centerId en JWT
        // no puede fijar el centro aquí — la selección de centro para
        // SUPER_ADMIN queda fuera del scope de sección 6.
        startDateTime: startDateTime.toISOString(),
        slotsCount: formData.slotsCount,
        status: 'RESERVED',
        idempotencyKey,
        ...(staffIdForCreate ? { staffId: staffIdForCreate } : {}),
        ...(formData.variantId ? { variantId: formData.variantId } : {}),
        // 4.1 (R2.1, R5.3, planner-pending-staff-assignment): solo se envía
        // cuando el admin marcó el checkbox; sin marcar, el campo se omite del
        // body (comportamiento anterior intacto). Va DENTRO del payload
        // congelado (D2), de modo que un Reintentar en UNKNOWN reenvía la misma
        // elección; el backend no lo incluye en el hash canónico de idempotencia.
        ...(formData.pendingStaffAssignment ? { pendingStaffAssignment: true } : {}),
      };
      createBookingPayloadRef.current = requestBody;
    }

    const controller = new AbortController();
    const request = { controller, timedOut: false };
    createBookingRequestRef.current = request;
    // 6.1 (D10): boundary de 20.000 ms — el mismo que ya usa Planner para
    // otras llamadas. NO introducimos timeout global de Axios (R3.4).
    const timeoutId = setTimeout(() => {
      request.timedOut = true;
      controller.abort();
    }, 20000);

    setBookingUnknown(false);
    setBookingUnknownMessage('');
    setFormLoading(true);
    try {
      // Sección 3 (D2): el payload va congelado en createBookingPayloadRef
      // desde el primer intento; `pendingStaffAssignment` (merge de
      // planner-pending-staff-assignment) y `staffId` (OWN_ONLY STAFF, merge
      // de planner-staff-visibility-controls) ya están incluidos arriba en el
      // body congelado, así que un Reintentar reenvía la misma elección.
      await api.post('/bookings', requestBody, { signal: controller.signal });

      createBookingKeyRef.current = null;
      createBookingPayloadRef.current = null;
      toast.success(t('bookings.created') || 'Booking created');
      setShowBookingDialog(false);
      fetchData();
    } catch (error) {
      // 6.1 (D10): timeout/abort/transporte SIN respuesta → UNKNOWN. No
      // mostramos toast de error ni cerramos el modal: conservamos el
      // formulario y la key para que el usuario decida. Una respuesta
      // HTTP con cuerpo (4xx/5xx con JSON) sí cae al camino de error
      // explícito (toast + modal conservado).
      const aborted = createBookingRequestRef.current === request &&
        (request.timedOut || error?.code === 'ERR_CANCELED' || (!error?.response && controller.signal.aborted));
      if (aborted) {
        // Round-1 fix for review panel finding 2 (D10): we release
        // `formLoading` and surface UNKNOWN IMMEDIATELY. A stalled
        // reconciliation GET previously kept the spinner running
        // forever and the modal never returned control to the user.
        // The reconciliation GET runs in the background with its own
        // AbortController and 20 s timeout — its job is to refine the
        // UNKNOWN message ("la reserva se creó, pero…") and to commit
        // the matched booking into local state on a positive match
        // (round-1 finding 8). The user-visible UNKNOWN state is
        // decoupled from the reconciliation GET so it cannot block
        // recovery.
        setBookingUnknownMessage('No se pudo confirmar la reserva. Revisa el Planner antes de reintentar.');
        setBookingUnknown(true);
        if (createBookingRequestRef.current === request) {
          createBookingRequestRef.current = null;
          setFormLoading(false);
        }
        const reconController = new AbortController();
        const reconTimeoutId = setTimeout(() => reconController.abort(), 20000);
        const clientIdForRecon = formData.clientId;
        const keyForRecon = idempotencyKey;
        // Round-2 fix for review panel finding (UI-UX reconciling
        // indicator): flag the in-flight reconciliation GET so the
        // `booking-create-unknown` block can render a `Reconciling…`
        // sub-label while we are still talking to the server. Cleared
        // in `.finally()` so the indicator always disappears when the
        // GET settles — success, failure, or timeout.
        setBookingReconciling(true);
        api.get(`/bookings?client_id=${clientIdForRecon}`, { signal: reconController.signal })
          .then((response) => {
            const reconciled = response?.data;
            const matches = Array.isArray(reconciled)
              ? reconciled.find((b) => b && b.idempotencyKey === keyForRecon)
              : null;
            if (matches) {
              // Round-1 fix for review panel finding 8: on positive
              // reconciliation, commit the booking to local state, close
              // the dialog, refresh, and surface a success toast. Only
              // the no-match branch should remain in UNKNOWN.
              // §4/D6: mutación local de bookings fuera de fetchData ⇒ invalida la firma.
              lastDaySignatureRef.current = null;
              setBookings((prev) => {
                if (prev.some((b) => b && b.id === matches.id)) return prev;
                return [...prev, matches];
              });
              createBookingKeyRef.current = null;
              createBookingPayloadRef.current = null;
              setBookingUnknown(false);
              setBookingUnknownMessage('');
              setShowBookingDialog(false);
              toast.success(t('bookings.created') || 'Booking created');
              fetchData();
            } else {
              setBookingUnknownMessage('No se pudo confirmar la reserva. Revisa el Planner antes de reintentar.');
            }
          })
          .catch(() => {
            setBookingUnknownMessage('No se pudo comprobar si la reserva se creó. Revisa el Planner antes de reintentar.');
          })
          .finally(() => {
            clearTimeout(reconTimeoutId);
            setBookingReconciling(false);
          });
      } else {
        // 6.1 §3.3 (R2.1-R2.3, R3.1-R3.3, D3): 4-way routing for a response
        // WITH a body. Reuses `classifyRequestError` rather than inlining a
        // status list (D3). The two idempotency 409s NEVER surface their raw
        // `detail` string — always the friendly, fixed text.
        const detail = error.response?.data?.detail;
        if (error.response?.status === 409 && detail === 'idempotency_key_payload_mismatch') {
          // (1) Payload mismatch on an in-flight/replayed key: discard both
          // refs — the corrected resubmit must be a brand-new operation
          // with a new key (R2.1/R2.2 analogue). Stay in the normal
          // editable dialog state; do NOT enter UNKNOWN.
          toast.error('Esta operación ya se había iniciado con otros datos. Para evitar duplicados, no podemos reutilizarla. Cancela y vuelve a crearla con los datos correctos.');
          createBookingKeyRef.current = null;
          createBookingPayloadRef.current = null;
        } else if (error.response?.status === 409 && detail === 'idempotency_in_progress') {
          // (2) The previous attempt is still being processed server-side.
          // Conserve both refs so Reintentar resends the identical
          // operation; surface the existing UNKNOWN surface (R2.3).
          setBookingUnknownMessage('Estamos comprobando si la operación anterior llegó a completarse. Espera unos segundos y pulsa ‘Reintentar’.');
          setBookingUnknown(true);
        } else if (classifyRequestError(error) === 'transient') {
          // (3) 408/429/5xx with a body: transient per steering — conserve
          // refs, enter UNKNOWN with the existing generic message.
          setBookingUnknownMessage('No se pudo confirmar la reserva. Revisa el Planner antes de reintentar.');
          setBookingUnknown(true);
        } else {
          // (4) Definitive 4xx: existing behavior, plus discard both refs
          // (R2.1) so a corrected resubmit sends a NEW key (R2.2).
          toast.error(error.response?.data?.detail || 'Failed to create booking');
          createBookingKeyRef.current = null;
          createBookingPayloadRef.current = null;
        }
      }
    } finally {
      clearTimeout(timeoutId);
      if (createBookingRequestRef.current === request) {
        createBookingRequestRef.current = null;
        setFormLoading(false);
      }
    }
  };

  // Update booking status
  const handleUpdateStaff = async () => {
    if (!selectedBooking) return;

    // All active bookings in the same service at the same start time share the same staff
    const selStart = parseISO(selectedBooking.startDateTime);
    const sameSlotBookings = bookings.filter(b => {
      if (b.calendarId !== selectedBooking.calendarId) return false;
      if (b.isPlaceholder) return false;
      if (b.status === 'CANCELLED' || b.status === 'NO_SHOW') return false;
      const bStart = parseISO(b.startDateTime);
      return bStart.getHours() === selStart.getHours() &&
             bStart.getMinutes() === selStart.getMinutes();
    });

    setFormLoading(true);
    try {
      await Promise.all(
        sameSlotBookings.map(b => api.put(`/bookings/${b.id}`, { staffId: selectedStaffId }))
      );

      toast.success(t('success.planner.staffUpdated'));
      setShowStatusDialog(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al actualizar el personal';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  // 4.3 (R3, R5.2, R5.3): dispara la asignación de personal para una reserva
  // PENDING vía POST /bookings/{id}/assign-staff — endpoint dedicado
  // (D2), distinto del PUT de reasignación de `handleUpdateStaff`.
  // Body vacío = aleatorio; `{staffId}` = elección explícita del admin.
  const handleAssignStaff = async () => {
    if (!selectedBooking) return;
    setFormLoading(true);
    try {
      const body = assignStaffChoice && assignStaffChoice !== 'auto'
        ? { staffId: assignStaffChoice }
        : {};
      await api.post(`/bookings/${selectedBooking.id}/assign-staff`, body);
      toast.success(t('success.planner.staffAssigned'));
      setShowStatusDialog(false);
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 400 && detail === 'no_staff_compatible') {
        // La reserva sigue PENDING: no se toca el estado local, solo se
        // avisa. El diálogo permanece abierto para que el admin reintente.
        toast.error(t('success.planner.noCompatibleStaff'));
      } else if (status === 409) {
        // Carrera / ya asignada por otra vía: refrescar para reflejar el
        // estado real (D3/D4).
        toast.error(t('success.planner.bookingAlreadyHasStaff'));
        setShowStatusDialog(false);
        fetchData();
      } else {
        toast.error(detail || 'Error al asignar personal');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (status === 'NO_SHOW' && !noShowReason.trim()) {
      toast.error(t('bookings.noShowReasonRequired'));
      return;
    }

    setFormLoading(true);
    try {
      await api.put(`/bookings/${selectedBooking.id}`, {
        status,
        noShowReason: status === 'NO_SHOW' ? noShowReason : null,
      });

      toast.success(t('planner.toast.statusUpdated'));
      setShowStatusDialog(false);
      fetchData();
    } catch (error) {
      toast.error(t('planner.toast.statusError'));
    } finally {
      setFormLoading(false);
    }
  };

  // Delete booking
  const handleDeleteBooking = async () => {
    const target = selectedBooking;
    if (!target) return;
    setFormLoading(true);

    // Remoción optimista local (D5.1/R4): quita SOLO el booking de la lista antes
    // del refetch, para que el re-render venga de aquí y no de una cascada de
    // GETs. Guarda el estado previo para revertir si el DELETE falla. El resto de
    // la UI (diálogo/selección) conserva su semántica anterior: se cierra al
    // confirmar el éxito, y en fallo el diálogo permanece abierto.
    const prevBookings = bookings;
    // §4/D6: mutación local de bookings fuera de fetchData ⇒ invalida la firma,
    // para que un payload idéntico posterior del servidor no quede enmascarado.
    lastDaySignatureRef.current = null;
    setBookings(prev => prev.filter(b => b.id !== target.id));

    try {
      await api.delete(`/bookings/${target.id}`);
      setShowStatusDialog(false);
      setSelectedBooking(null);
      toast.success(t('planner.toast.bookingDeleted'));
      // R7: refresh redundante evitado — antes se refrescaba dos veces; ahora un
      // único fetchData de corrección de fondo tras la remoción optimista.
      console.debug('[planner] redundant refresh avoided (optimistic delete)');
      fetchData();
    } catch (error) {
      // Revertir la remoción optimista: el booking reaparece. El diálogo sigue
      // abierto (semántica previa) para que el usuario vea el error en contexto.
      // §4/D6: la reversión también escribe bookings fuera de fetchData.
      lastDaySignatureRef.current = null;
      setBookings(prevBookings);
      toast.error(t('planner.toast.bookingDeleteError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedBooking) return;
    setNoteLoading(true);
    try {
      await api.patch(`/bookings/${selectedBooking.id}/internal-note`, { note: internalNote });
      setSelectedBooking(b => ({ ...b, internalNote }));
      // Update the booking in the local list so the flag icon updates immediately
      // §4/D6: mutación local de bookings fuera de fetchData ⇒ invalida la firma.
      lastDaySignatureRef.current = null;
      setBookings(prev => prev.map(b => b.id === selectedBooking.id ? { ...b, internalNote } : b));
      toast.success(t('success.planner.noteSaved'));
    } catch (error) {
      toast.error(t('success.planner.noteSaveError'));
    } finally {
      setNoteLoading(false);
    }
  };

  // Drag handlers
  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, time, calendarId) => {
    e.preventDefault();
    if (!draggedBooking) return;

    const newStartDateTime = new Date(selectedDate);
    newStartDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);

    // §4/R4.2/D4: mantén la pausa del refresco durante la ventana del PUT.
    dropInFlightRef.current = true;
    try {
      await api.put(`/bookings/${draggedBooking.id}`, {
        calendarId,
        startDateTime: newStartDateTime.toISOString(),
      });
      toast.success(t('bookings.moveBooking'));
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to move booking';
      toast.error(message);
    } finally {
      dropInFlightRef.current = false;
    }

    setDraggedBooking(null);
  };

  // Filter clients by search
  const filteredClients = clients.filter(client => {
    const nameHaystack = normalizeForSearch(`${client.fullName} ${client.surname || ""}`.trim());
    if (nameHaystack.includes(normalizeForSearch(clientSearch))) return true;
    const phoneQuery = normalizePhone(clientSearch);
    return phoneQuery.length > 0 && normalizePhone(client.phone).includes(phoneQuery);
  });

  // Get status class
  const getStatusClass = (status, isDebt) => {
    if (isDebt) return 'status-debt';
    switch (status) {
      case 'ATTENDED': return 'status-attended';
      case 'NO_SHOW': return 'status-noshow';
      default: return 'status-reserved';
    }
  };

  if (loading && !center) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  // Primera carga sin snapshot previo (2.3/R2): si el primer fetch falla con un
  // modo transitorio, salir del loading y ofrecer reintento — nunca loading
  // eterno ni una vista vacía sin acción.
  // R5.1/D9: un horario de centro inválido usa el MISMO mecanismo (mismo
  // estado, mismo reintento) con un mensaje propio: es un error local de
  // datos, no un problema de conexión ni de autenticación.
  if (plannerError && !center) {
    const invalidSchedule = plannerError.kind === 'invalid-schedule';
    return (
      <div
        className="flex flex-col items-center justify-center h-full gap-4 text-center px-6"
        data-testid="planner-error-coldstart"
      >
        <AlertTriangle className="w-8 h-8 text-amber-400" />
        <p className="text-sm text-zinc-300 max-w-sm" data-testid="planner-error-message">
          {invalidSchedule
            ? 'El horario del centro no es válido, así que no se puede preparar la agenda.'
            : 'No se pudo cargar el planner. Comprueba tu conexión e inténtalo de nuevo.'}
        </p>
        <Button
          variant="outline"
          onClick={() => plannerError.retry()}
          data-testid="planner-retry-btn"
        >
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="planner-page">
      {/* Banner de error recuperable no bloqueante (R2): conserva los bookings
          ya cargados y ofrece un reintento explícito, sin auto-retry. */}
      {plannerError && (
        <div
          className="flex items-center justify-between gap-3 bg-amber-950/80 text-amber-200 text-sm px-4 py-2 border-b border-amber-800"
          data-testid="planner-error-banner"
        >
          <span>No se pudieron actualizar los datos. Los datos mostrados pueden no estar al día.</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => plannerError.retry()}
            data-testid="planner-retry-btn"
          >
            Reintentar
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="planner-header px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-xl font-bold tracking-tight uppercase">
              {t('planner.title')}
            </h1>
            
            {/* Date navigation */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={goToPrevious} data-testid="prev-day-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="font-medium" data-testid="date-picker-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(selectedDate, 'EEEE, d MMMM', { locale })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => { if (date) { setSelectedDate(date); setCalendarOpen(false); } }}
                    locale={locale}
                    weekStartsOn={language === 'en' ? 0 : 1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <Button variant="ghost" size="icon" onClick={goToNext} data-testid="next-day-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-btn">
              {t('planner.today')}
            </Button>

            <div className="flex items-center border border-zinc-700 rounded-sm overflow-hidden">
              {[
                { key: 'day',   label: 'Día' },
                { key: 'week',  label: 'Semana' },
                { key: 'month', label: 'Mes' },
                { key: 'staff', label: 'Staff' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    viewMode === key
                      ? 'bg-zinc-700 text-zinc-100'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {user?.role === 'CENTER_ADMIN' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setExportTemplateName(format(selectedDate, 'EEEE d MMMM', { locale }));
                  setShowExportDialog(true);
                }}
                data-testid="export-template-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Exportar como plantilla
              </Button>
            )}
          </div>

        </div>
      </div>

      {viewMode === 'week' && (
        <WeekView
          selectedDate={selectedDate}
          services={services}
          center={center}
          onBookingMoved={fetchData}
        />
      )}
      {viewMode === 'month' && (
        <MonthView
          selectedDate={selectedDate}
          onSelectDate={(date) => { setSelectedDate(date); setViewMode('day'); }}
          center={center}
        />
      )}
      {viewMode === 'staff' && (
        <StaffDayView
          selectedDate={selectedDate}
          services={services}
          center={center}
          staff={staffForView}
          onNewBooking={handleNewBookingForStaff}
          onBookingClick={handleBookingClick}
        />
      )}
      {viewMode === 'day' && (
      <div ref={scrollRef} className="flex-1 overflow-auto planner-scroll">
        <div className="min-w-[800px]">
          {isClient && services.length === 0 ? (
            <div data-testid="planner-client-empty-services" className="p-8 text-center">
              <p className="text-sm font-medium text-zinc-200">No tienes servicios disponibles</p>
              <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
                Todavía no tienes ningún bono o cuota activa que te dé acceso a un servicio. Los servicios aparecerán aquí en cuanto el centro active tu acceso — contacta con el centro si crees que se trata de un error.
              </p>
            </div>
          ) : (
          <>
            {/* D7.1 — header block (master service row + variant columns row)
                lives inside ONE sticky surface at top:0 so the master service
                header stays visible above the persistent variant columns
                without either row covering the other. The two inner rows are
                plain flex containers (no `sticky` of their own); the wrapper
                owns the sticky behaviour, so this won't drift if the master
                header's height changes. */}
            <div data-testid="planner-header-sticky" className="sticky top-0 z-40 bg-background/95 backdrop-blur-md">
            {/* Services header */}
            <div className="flex border-b border-zinc-800">
              <div className="w-20 flex-shrink-0 px-2 py-3 border-r border-zinc-800">
                <span className="text-xs text-zinc-500 uppercase tracking-wide">{t('planner.time')}</span>
              </div>
              {services.map((service) => (
                <div
                  key={service.id}
                  className="flex-1 min-w-[180px] px-3 py-3 border-r border-zinc-800 last:border-r-0"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: service.color }}
                    />
                    <span className="font-display font-semibold text-sm uppercase tracking-wide">
                      {service.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-xs text-zinc-500">
                      {t('planner.capacity')}: {service.capacityPerSlot} • {(service.slotsPerBooking || 2) * SLOT_DURATION}min
                    </div>
                    {user?.role === 'CENTER_ADMIN' && (
                      <button
                        type="button"
                        onClick={() => {
                          setApplyingService({ id: service.id, name: service.name });
                          setSelectedTemplateId('');
                          const initialDate = format(selectedDate, 'yyyy-MM-dd');
                          setApplyStartDate(initialDate);
                          setApplyEndDate(initialDate);
                          setApplyValidationError('');
                          setApplyError(null);
                          setApplyResult(null);
                          setShowApplyDialog(true);
                        }}
                        className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-200 transition-colors px-1.5 py-0.5 rounded hover:bg-zinc-800"
                        title={t('planner.applyTemplate')}
                      >
                        <CalendarCheck className="w-3 h-3" />
                        {t('planner.template')}
                      </button>
                    )}
                  </div>
                  {user?.role === 'CENTER_ADMIN' && (() => {
                    const stats = dailyServiceStats[service.id] || { attended: 0, reserved: 0 };
                    return (
                      <div className="text-[10px] text-zinc-600 mt-1 tabular-nums">
                        <span className="text-green-500">{stats.attended} {t('bookings.status.attended').toLowerCase()}</span>
                        <span className="mx-1 text-zinc-700">·</span>
                        <span className="text-blue-400">{stats.reserved} {t('bookings.status.reserved').toLowerCase()}</span>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>

            {/* 6.2 (D7.1, D8.3) — second header row emitting one cell per
                variant column, ONCE under the master service header. The
                header cell carries `variant.color` (fallback `service.color`)
                via `resolveLaneHeaderColor` and is the ONLY place variant
                color is rendered (R9, D8). It only renders when at least one
                service has persistent variant columns so legacy/feature-OFF
                views remain bit-identical. */}
            {hasAnyLaneColumns && (
              <div className="flex border-b border-zinc-800">
                <div className="w-20 flex-shrink-0 px-2 py-1 border-r border-zinc-800" />
                {services.map((service) => {
                  const columns = serviceLaneColumns[service.id] || [];
                  if (columns.length === 0) {
                    return (
                      <div
                        key={service.id}
                        className="flex-1 min-w-[180px] border-r border-zinc-800 last:border-r-0"
                      />
                    );
                  }
                  return (
                    <div
                      key={service.id}
                      className="flex-1 min-w-[180px] flex border-r border-zinc-800 last:border-r-0"
                      data-testid={`variant-columns-header-${service.id}`}
                    >
                      {columns.map((column) => {
                        const headerColor = resolveLaneHeaderColor(service, {
                          variantId: column.variantId,
                        });
                        // 6.7 (D9): legacy/null never shows a "+" (it's a
                        // read-only column while feature ON + active variants).
                        // Header label: legacy has none (R7.3); unknown has
                        // none (D7.5 — no invented metadata).
                        const label = column.variant?.name || '';
                        return (
                          <div
                            key={column.key}
                            className="flex-1 min-w-0 px-2 py-1 border-r border-zinc-800/40 last:border-r-0"
                            data-testid={`variant-column-header-${service.id}-${column.key}`}
                            data-variant-id={column.variantId ?? ''}
                            data-lane-kind={
                              column.isLegacy
                                ? 'legacy'
                                : column.isUnknown
                                ? 'unknown'
                                : column.isInactive
                                ? 'inactive'
                                : 'active'
                            }
                          >
                            <div className="flex items-center gap-1">
                              <span
                                aria-hidden="true"
                                className="w-2 h-2 rounded-sm flex-shrink-0"
                                style={{ backgroundColor: headerColor || service.color }}
                              />
                              <span className="text-[10px] text-zinc-400 truncate">
                                {label || (column.isLegacy ? '—' : '·')}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
            </div>

            {/* Time slots — row windowing (D6, R5): solo se montan las filas
                visibles; los spacers preservan el scrollHeight completo. */}
            {rowWindow.topPad > 0 && (
              <div key="rw-top-spacer" aria-hidden="true" style={{ height: `${rowWindow.topPad}px` }} />
            )}
            {timeSlots.slice(rowWindow.startIndex, rowWindow.endIndex).map((time, i) => {
              const index = rowWindow.startIndex + i;
              const timeStr = format(time, 'HH:mm');
              const isHourStart = time.getMinutes() === 0;

              return (
                <div
                  key={index}
                  className={`flex ${isHourStart ? 'border-t border-zinc-700' : ''}`}
                >
                  {/* Time column */}
                  <div className="w-20 flex-shrink-0 planner-time-col px-2 py-1 text-right">
                    <span className={`text-xs tabular-nums ${isHourStart ? 'text-zinc-300 font-medium' : 'text-zinc-600'}`}>
                      {timeStr}
                    </span>
                  </div>
                  
                  {/* Service columns */}
                  {services.map((service) => {
                    const capacity = getCellCapacity(time, service.id);
                    const cellBookings = getBookingsForCell(time, service.id);
                    const isFull = capacity.used >= capacity.total;
                    // Section 4 (R2/D4): pass the cell's (calendarId, variantId)
                    // so a per-service or per-variant break only marks THIS
                    // cell. Sublanes (PlannerVariantLanes) further refine
                    // per-variant breaks within the same service.
                    const inBreak = isBreakTime(time, service.id);
                    const hhmm = format(time, 'HH:mm');
                    const blockedReason = availabilityMap?.__blocked?.[service.id]?.[hhmm]
                      || (!isClient ? getAdminBlockedReason(time, service.id) : undefined);

                    // Only show booking if it starts at this slot
                    const startingBookings = cellBookings.filter(b => {
                      const bStart = parseISO(b.startDateTime);
                      return bStart.getHours() === time.getHours() &&
                             bStart.getMinutes() === time.getMinutes();
                    });

                    // CLIENT: suppress NO_STAFF overlay whenever any booking is visible (own or placeholder)
                    const hasOwnBooking = isClient && cellBookings.some(b => !b.isPlaceholder);
                    const hasCellBookings = cellBookings.length > 0;
                    const staffAvailable = availabilityMap?.__staff?.[service.id]?.[hhmm] ?? 1;
                    const isClientBlocked = isClient && (!!blockedReason || staffAvailable === 0) && !hasOwnBooking;
                    const renderBookingCard = (booking, idx, bookingCount) => {
                      const slots = Math.ceil(booking.durationMinutes / SLOT_DURATION);
                      const height = slots * 48 - 4; // 48px per slot minus padding
                      const count = Math.max(1, bookingCount);
                      const widthPct = 100 / count;
                      const leftPct = idx * widthPct;
                      const gutter = 4; // px
                      // 6.5 (R9, D8): booking cards MUST NOT carry variant
                      // color. Functional colors (bono / estado / deuda) live
                      // exclusively in `getStatusClass`. The previous
                      // `backgroundColor`/`borderLeftColor` derived from
                      // variant color is removed — variant identity belongs
                      // ONLY to the persistent column header.
                      // Reuse the shared temporal precompute (D6, R5): no
                      // parseISO in the booking-node render.
                      const bt = bookingTemporals.get(booking.id);

                      return (
                        <div
                          key={booking.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, booking)}
                          onDragEnd={() => setDraggedBooking(null)}
                          onClick={(e) => handleBookingClick(booking, e)}
                          className={`booking-card ${getStatusClass(booking.status, booking.isDebt)}`}
                          style={{
                            height: `${height}px`,
                            top: '2px',
                            left: `calc(${leftPct}% + ${gutter / 2}px)`,
                            width: `calc(${widthPct}% - ${gutter}px)`,
                          }}
                          data-testid={`booking-${booking.id}`}
                        >
                          <div className="flex items-center gap-1 min-w-0">
                            <span
                              className="font-medium leading-tight whitespace-normal break-words min-w-0 hover:underline cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); if (booking.clientId) navigate(`/clients/${booking.clientId}`); }}
                            >{formatClientName(booking.clientName)}</span>
                            {booking.isDebt && (
                              <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                            )}
                            {!isClient && booking.internalNote && (
                              <Flag className="w-3 h-3 text-orange-400 flex-shrink-0" />
                            )}
                            {!isClient && booking.isNewClient && (
                              <Star className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                          <div className="text-[10px] opacity-70 truncate">
                            {bt ? bt.startLabel : format(parseISO(booking.startDateTime), 'HH:mm')} -
                            {bt ? bt.endLabelCell : format(addMinutes(parseISO(booking.startDateTime), booking.durationMinutes), 'HH:mm')}
                          </div>
                          {booking.staffId && (
                            <div className="text-[10px] opacity-70 truncate">
                              {booking.staffName || staffById[booking.staffId]?.fullName || ''}
                            </div>
                          )}
                          {/* 4.2 (R1.3, R1.4, R5.1): distintivo de "pendiente
                              de asignar personal" — se activa SOLO por
                              `staffAssignmentStatus === "PENDING"`, nunca por
                              la mera ausencia de `staffId` (una reserva
                              legacy sin staff no lleva este flag y por tanto
                              no se pinta como pendiente). */}
                          {booking.staffAssignmentStatus === 'PENDING' && (
                            <div
                              className="text-[10px] font-medium text-amber-400 truncate"
                              data-testid={`booking-pending-badge-${booking.id}`}
                            >
                              Pendiente de asignar
                            </div>
                          )}
                        </div>
                      );
                    };
                    // 2.6 (R5/D6): when sublanes are rendered, the parent
                    // cell's own "+" is suppressed — each sublane renders its
                    // own "+" gated by `laneHasRemainingCapacity`. The parent
                    // cell (HH:mm|calendarId) remains the unique drop target
                    // and backend staff/pin target (out of scope here).
                    // 6.1/6.3 (D7): the column set comes from the parent
                    // service+day memo, not from the slot's bookings — the
                    // columns are persistent and stable across every time row.
                    const dayColumns = serviceLaneColumns[service.id] || [];
                    const sublanesActive = dayColumns.length > 0;
                    // Section 4 (R4/4.4): per-sublane "+" gating is now done
                    // inside PlannerVariantLanes via `isBreakForLane`. Here
                    // we only suppress when the whole cell is client-blocked;
                    // a per-variant break is no longer enough to suppress
                    // sibling lanes' "+" (a lane without a break matching its
                    // own variantId stays addable).
                    const sublaneAddClick = !isClientBlocked
                      ? (variantId) => handleQuickAddClick(time, service.id, variantId)
                      : undefined;

                    return (
                      <div
                        key={service.id}
                        className={`flex-1 min-w-[180px] planner-cell relative ${isFull ? 'bg-zinc-900/50' : ''}`}
                        onClick={(e) => {
                          if (e.target.closest('[data-testid^="booking-"]')) return;
                          if (!isFull && !inBreak && !isClientBlocked) handleCellClick(time, service.id);
                        }}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, time, service.id)}
                        data-testid={`cell-${timeStr}-${service.id}`}
                      >
                        {/* Staff availability overlays — suppressed when bookings visible or slot is a break */}
                        {blockedReason === 'STAFF_OFF_DAY' && !hasCellBookings && !inBreak && !(isClient && staffAvailable === 0) && (
                          <div className="absolute inset-0 bg-zinc-900/60 z-10 flex items-center justify-center pointer-events-none">
                            <span className="text-[10px] text-zinc-500 uppercase tracking-widest">DÍA LIBRE</span>
                          </div>
                        )}
                        {blockedReason === 'STAFF_OUTSIDE_WORKING_HOURS' && !hasCellBookings && !inBreak && !(isClient && staffAvailable === 0) && (
                          <div className="absolute inset-0 bg-indigo-900/40 z-10 flex items-center justify-center pointer-events-none">
                            <span className="text-[10px] text-indigo-400 uppercase tracking-widest">FUERA HORARIO</span>
                          </div>
                        )}
                        {blockedReason === 'NO_STAFF_AVAILABLE' && !hasCellBookings && !inBreak && !isClient && (
                          <div className="absolute inset-0 bg-amber-900/40 z-10 flex items-center justify-center pointer-events-none">
                            <span className="text-[10px] text-amber-300 uppercase tracking-widest">SIN PERSONAL</span>
                          </div>
                        )}
                        {isClient && (blockedReason === 'NO_STAFF_AVAILABLE' || staffAvailable === 0) && !hasCellBookings && !inBreak && (
                          <div className="absolute inset-0 bg-zinc-900/60 z-10 flex items-center justify-center pointer-events-none">
                            <span className="text-[10px] text-zinc-500 uppercase tracking-widest">No disponible</span>
                          </div>
                        )}

                        {/* Break overlay — always on top of NO STAFF.
                            Section 4 (R4/4.4): when sublanes are active, the
                            parent cell's chip is suppressed (each lane renders
                            its own per-variant chip). With sublanes inactive,
                            the legacy cell-wide overlay is preserved bit-for-bit. */}
                        {inBreak && !sublanesActive && (
                          <div
                            className={`absolute inset-0 bg-zinc-900/75 z-20 flex items-center justify-center ${!isClient ? 'cursor-pointer hover:bg-zinc-800/75' : 'pointer-events-none'}`}
                            onClick={!isClient ? (e) => { e.stopPropagation(); handleBreakCellClick(time, service.id); } : undefined}
                          >
                            <span className="text-[10px] text-zinc-600 uppercase tracking-widest">{t('planner.break')}</span>
                            {!isClient && <X className="w-3 h-3 text-zinc-700 ml-1" />}
                          </div>
                        )}

                        {/* Quick add button — admins can add even on blocked slots; clients cannot.
                            Hidden when sublane mode is active; per-sublane "+"s handle
                            capacity per lane via `laneHasRemainingCapacity` (R5/D6). */}
                        {!sublanesActive && !isFull && !inBreak && !isClientBlocked && (
                          <button
                            type="button"
                            className="absolute top-1 left-1 z-20 rounded bg-zinc-800/70 px-1.5 py-0.5 text-[10px] text-zinc-200 hover:bg-zinc-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickAddClick(time, service.id);
                            }}
                            title={t('planner.addBooking')}
                          >
                            +
                          </button>
                        )}

                        {/* Capacity indicator */}
                        {capacity.used > 0 && !inBreak && (
                          <div className="absolute top-1 right-1 text-[10px] text-zinc-500 tabular-nums">
                            {capacity.used}/{capacity.total}
                          </div>
                        )}

                        {/* Bookings */}
                        {sublanesActive
                          ? (
                            <PlannerVariantLanes
                              service={service}
                              bookings={startingBookings}
                              bookingsInCell={cellBookings}
                              renderBooking={renderBookingCard}
                              onAddClick={sublaneAddClick}
                              columns={dayColumns}
                              isBreakForLane={(variantId) => isBreakTime(time, service.id, variantId)}
                              // Section 4 review fix round 1 (R4.5): restore
                              // the legacy click-to-delete interaction on the
                              // per-lane break chip. Non-client users click
                              // the lane's chip → D2-aware resolver picks
                              // the per-variant break that applies to this
                              // lane's context.
                              onBreakLaneClick={!isClient ? (variantId) => handleBreakCellClick(time, service.id, variantId) : undefined}
                              // Section 4 review fix round 1 (R4.5): share
                              // the i18n-resolved label with the parent
                              // chip so non-Spanish locales don't render
                              // "DESCANSO" inside sublanes.
                              breakLabel={t('planner.break')}
                            />
                          )
                          : startingBookings.map((booking, idx) => renderBookingCard(
                            booking,
                            idx,
                            startingBookings.length,
                          ))}

                        {/* Add button for empty slots - only show when no bookings and not a break.
                            2.8 (R1.3 / QA finding #2): when sublanes are active, only the per-sublane "+"s
                            may render — the parent cell's hover "+" is suppressed. With feature OFF or
                            sublanes inactive the parent "+" is preserved exactly as before. */}
                        {!sublanesActive && !isFull && !inBreak && cellBookings.length === 0 && (
                          <button
                            data-testid="quick-add-hover"
                            className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity z-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickAddClick(time, service.id);
                            }}
                          >
                            <Plus className="w-4 h-4 text-zinc-500" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {rowWindow.bottomPad > 0 && (
              <div key="rw-bottom-spacer" aria-hidden="true" style={{ height: `${rowWindow.bottomPad}px` }} />
            )}
          </>
          )}
        </div>

      </div>
      )}

      {/* Quick Add Dialog */}
      {/* Create Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {t('planner.addBooking')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('planner.addBooking')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* 6.1 §3.2 (R1.1, D1): while UNKNOWN, ALL payload fields
                (client, service/calendar, time, slots, staff, variant) are
                non-editable. `disabled` on a `<fieldset>` cascades to every
                descendant native/Radix `<button>`/`<input>` in real
                browsers — verified by e2e (§5), not by this jsdom suite:
                jsdom's `.disabled` does not reflect the fieldset cascade.
                The UNKNOWN action row and the normal Actions row stay
                OUTSIDE this fieldset so Reintentar/Cancelar remain
                clickable. */}
            <fieldset disabled={bookingUnknown} className="space-y-4 min-w-0 border-0 p-0 m-0">
            {/* Client search — hidden for CLIENT role (self is auto-selected) */}
            {!isClient && (
            <div className="space-y-2">
              <Label>{t('planner.selectClient')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={clientSearch}
                  onChange={(e) => { setClientSearch(e.target.value); setClientHighlight(-1); }}
                  onKeyDown={(e) => {
                    if (!clientSearch || filteredClients.length === 0) return;
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setClientHighlight(i => Math.min(i + 1, filteredClients.length - 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setClientHighlight(i => Math.max(i - 1, 0));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const idx = clientHighlight >= 0 ? clientHighlight : 0;
                      const client = filteredClients[idx];
                      if (client) { setFormData(f => ({ ...f, clientId: client.id })); setClientSearch(client.fullName); setClientHighlight(-1); }
                    } else if (e.key === 'Escape') {
                      setClientSearch(''); setClientHighlight(-1);
                    }
                  }}
                  placeholder={t('common.search')}
                  className="pl-9 input-tactical"
                  data-testid="client-search-input"
                />
              </div>

              {clientSearch && (
                <div className="max-h-40 overflow-y-auto border border-zinc-800 rounded-sm">
                  {filteredClients.length === 0 ? (
                    <div className="p-3 text-sm text-zinc-500">{t('common.noResults')}</div>
                  ) : (
                    filteredClients.map((client, idx) => (
                      <button
                        key={client.id}
                        onClick={() => {
                          setFormData(f => ({ ...f, clientId: client.id }));
                          setClientSearch(client.fullName);
                          setClientHighlight(-1);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-800 transition-colors ${
                          idx === clientHighlight || formData.clientId === client.id ? 'bg-zinc-800' : ''
                        }`}
                        data-testid={`client-option-${client.id}`}
                      >
                        <div className="font-medium">{[client.fullName, (client.surname || "").trim()].filter(Boolean).join(' ')}</div>
                        <div className="text-xs text-zinc-500">{client.phone}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            )}

            {/* Service select */}
            <div className="space-y-2">
              <Label>{t('planner.selectService')}</Label>
              <Select
                value={formData.calendarId}
                onValueChange={(value) => {
                  const service = services.find(s => s.id === value);
                  setFormData(f => ({ 
                    ...f, 
                    calendarId: value,
                    slotsCount: service?.slotsPerBooking || 2,
                  }));
                }}
              >
                <SelectTrigger data-testid="service-select">
                  <SelectValue placeholder={t('planner.selectService')} />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: service.color }} />
                        {service.name} ({(service.slotsPerBooking || 2) * SLOT_DURATION}min)
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('planner.startTime')}</Label>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(f => ({ ...f, startTime: e.target.value }))}
                  className="input-tactical"
                  step="900"
                  data-testid="start-time-input"
                />
              </div>
              {!isClient && (
              <div className="space-y-2">
                <Label>{t('planner.slots') || 'Slots'}</Label>
                <Select
                  value={String(formData.slotsCount)}
                  onValueChange={(value) => setFormData(f => ({ ...f, slotsCount: Number(value) }))}
                >
                  <SelectTrigger data-testid="slots-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 6, 8].map((slots) => (
                      <SelectItem key={slots} value={String(slots)}>
                        {slots} slots = {slots * SLOT_DURATION}min
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>

            {/* 4.1 (R2.1, R5.3): checkbox "Pendiente de asignar personal" —
                visible SOLO para CENTER_ADMIN/SUPER_ADMIN (mismo criterio que
                `isAdminRole`). Al marcarlo se limpia `staffId` y se oculta el
                selector de staff de abajo (no tiene sentido elegir personal
                si se está pidiendo dejarlo pendiente). */}
            {isAdminRole && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="pending-staff-assignment-checkbox"
                  checked={!!formData.pendingStaffAssignment}
                  onCheckedChange={(checked) => setFormData(f => ({
                    ...f,
                    pendingStaffAssignment: checked === true,
                    ...(checked === true ? { staffId: '' } : {}),
                  }))}
                  data-testid="pending-staff-assignment-checkbox"
                />
                <Label
                  htmlFor="pending-staff-assignment-checkbox"
                  className="font-normal cursor-pointer"
                >
                  Pendiente de asignar personal
                </Label>
              </div>
            )}

            {/* Staff selector — admin only; oculto cuando la reserva se crea
                como pendiente de asignar personal (4.1). */}
            {!isClient && !formData.pendingStaffAssignment && staffUsers.filter(s => (s.serviceIds || []).includes(formData.calendarId)).length > 0 && (
              <div className="space-y-2">
                <Label>Personal asignado</Label>
                <Select
                  value={formData.staffId || 'auto'}
                  onValueChange={(v) => setFormData(f => ({ ...f, staffId: v === 'auto' ? '' : v }))}
                >
                  <SelectTrigger data-testid="new-booking-staff-select">
                    <SelectValue placeholder="Asignar automáticamente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Asignar automáticamente</SelectItem>
                    {staffUsers
                      .filter(s => (s.serviceIds || []).includes(formData.calendarId))
                      .map((staff) => (
                        <SelectItem key={staff.id} value={staff.id}>
                          {staff.fullName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Variant picker (D17) — visible to ADMIN, STAFF, and CLIENT
                when the active calendar has enablePlanningVariants=true and
                at least one variant is active. CLIENT self-service selects
                variants explicitly (no auto-select). The picker is filtered
                to active=true (D16) regardless of role. The previous
                `!isClient &&` gate contradicted D17 and was removed; see
                design.md D17 reaffirmation 2026-09-02 and BLOCKED.md B9.
                6.7 (R8, D9): the `Sin variante (legacy)` option is REMOVED
                when the feature is ON with active variants. Creating a
                booking without a variant is no longer possible through the
                Planner admin dialog. The legacy path stays in place when
                the feature is OFF (no active variants → no picker at all,
                so the dialog behaves exactly as before). */}
            {(() => {
              const activeService = services.find(s => s.id === formData.calendarId);
              const activeVariants = (activeService?.enablePlanningVariants && activeService?.variants) || [];
              const activeOnly = activeVariants.filter(v => v.active !== false);
              if (activeOnly.length === 0) return null;
              return (
                <div className="space-y-2" data-testid="variant-select">
                  <Label>Variante</Label>
                  <Select
                    value={formData.variantId || '__placeholder__'}
                    onValueChange={(v) => setFormData(f => ({
                      ...f, variantId: v === '__placeholder__' ? '' : v
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona variante" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeOnly.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            </fieldset>

            {/* 6.1 (D10): estado UNKNOWN tras timeout/abort. El modal se
                conserva con el formulario intacto; el usuario decide si
                reintentar (misma key, replay) o cancelar (descarta key). */}
            {bookingUnknown && (
              <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="booking-create-unknown" role="alert" aria-live="polite">
                <div className="text-sm text-amber-300">
                  {bookingUnknownMessage || 'No se pudo confirmar la reserva.'}
                </div>
                {/* Round-2 fix for review panel finding (UI-UX reconciling
                    indicator): show a transient `Reconciling…` sub-label
                    while the background reconciliation GET is in flight.
                    Mirrors the spinner convention used elsewhere in the
                    Planner (`Loader2` with `animate-spin`). The indicator
                    disappears when the GET settles — positive match
                    closes the dialog, no-match keeps UNKNOWN with the
                    cancel/retry pair available. data-testid follows the
                    existing pattern for downstream selectors. */}
                {bookingReconciling && (
                  <div
                    className="flex items-center gap-2 text-xs text-zinc-400"
                    data-testid="booking-create-reconciling"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Reconciling…</span>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowBookingDialog(false)}
                    data-testid="booking-unknown-close-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="btn-primary"
                    onClick={handleCreateBooking}
                    disabled={formLoading}
                    data-testid="booking-unknown-retry-btn"
                  >
                    Reintentar (misma operación)
                  </Button>
                </div>
              </div>
            )}

            {/* Actions — se oculta cuando el bloque UNKNOWN (D10) está visible
                para evitar dos filas de acciones primarias con el mismo handler. */}
            {!bookingUnknown && (
              <div className="flex items-center justify-between gap-2 pt-2">
                {!isClient && (
                  <Button
                    variant="ghost"
                    className="text-xs text-zinc-500 hover:text-zinc-300 px-2"
                    onClick={handleQuickAddBreak}
                    disabled={formLoading}
                  >
                    {formLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Crear descanso en su lugar
                  </Button>
                )}
                <div className="flex gap-2 ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => setShowBookingDialog(false)}
                    data-testid="cancel-booking-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="btn-primary"
                    onClick={handleCreateBooking}
                    disabled={formLoading || !formData.clientId || !isCreateVariantValid()}
                    data-testid="create-booking-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.create')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {selectedBooking?.clientId ? (
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() => { setShowStatusDialog(false); navigate(`/clients/${selectedBooking.clientId}`); }}
                >
                  {selectedBooking.clientName}
                </span>
              ) : (
                selectedBooking?.clientName
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('planner.bookingDetails') || 'Booking details'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Debt warning */}
              {selectedBooking.isDebt && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-amber-400">
                      {t('bookings.debtWarning') || 'Booking without paid pack'}
                    </div>
                    <div className="text-xs text-amber-400/70">
                      {t('bookings.debtNote') || 'Client has no active session pack. This booking counts toward debt limit.'}
                    </div>
                  </div>
                </div>
              )}

              {/* Booking info */}
              <div className="p-3 bg-zinc-900 rounded-sm space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: selectedBooking.calendarColor }}
                  />
                  <span className="font-medium">{selectedBooking.calendarName}</span>
                </div>
                <div className="text-xs text-zinc-500">
                  {format(parseISO(selectedBooking.startDateTime), 'EEEE, d MMMM • HH:mm', { locale })} - 
                  {format(addMinutes(parseISO(selectedBooking.startDateTime), selectedBooking.durationMinutes), 'HH:mm')}
                </div>
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium mt-2 ${getStatusClass(selectedBooking.status, selectedBooking.isDebt)}`}>
                  {t(`bookings.status.${selectedBooking.status.toLowerCase()}`)}
                  {selectedBooking.isDebt && <AlertTriangle className="w-3 h-3 ml-1" />}
                </div>
              </div>

              {/* Staff info for CLIENT (read-only) */}
              {user?.role === 'CLIENT' && selectedBooking.staffId && (
                <div className="text-xs text-zinc-400">
                  {t('bookings.assignedStaff')}: {selectedBooking.staffName || selectedBooking.staffId}
                </div>
              )}


              {/* 4.3 (R3, R5.2, R5.3): acción "Asignar personal" para una
                  reserva PENDING — visible SOLO para CENTER_ADMIN/SUPER_ADMIN
                  (isAdminRole). Usa el endpoint dedicado POST
                  /bookings/{id}/assign-staff (D2), no el PUT de
                  reasignación de abajo. `auto` = body vacío (aleatorio);
                  cualquier otro valor = `{staffId}` explícito. */}
              {isAdminRole && selectedBooking.staffAssignmentStatus === 'PENDING' && (
                <div
                  className="space-y-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm"
                  data-testid="pending-staff-assignment-block"
                >
                  <div className="text-sm font-medium text-amber-400">
                    Pendiente de asignar personal
                  </div>
                  <Select
                    value={assignStaffChoice}
                    onValueChange={setAssignStaffChoice}
                  >
                    <SelectTrigger data-testid="assign-staff-select">
                      <SelectValue placeholder="Asignar automáticamente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Asignar automáticamente</SelectItem>
                      {staffUsers
                        .filter(staff => (staff.serviceIds || []).includes(selectedBooking.calendarId))
                        .map((staff) => (
                          <SelectItem key={staff.id} value={staff.id}>
                            {staff.fullName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    className="w-full border-amber-600 text-amber-400 hover:bg-amber-600/10"
                    onClick={handleAssignStaff}
                    disabled={formLoading}
                    data-testid="assign-staff-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Asignar personal
                  </Button>
                </div>
              )}

              {/* Assigned staff — bloque genérico de reasignación (PUT).
                  4.4-fix R5.2/D2: oculto cuando el admin ya ve el bloque
                  dedicado "Asignar personal" de arriba (misma condición:
                  isAdminRole && PENDING), para no ofrecer dos controles
                  competidores sin jerarquía. STAFF sigue viendo este
                  selector incluso sobre una reserva PENDING (no tiene acceso
                  al bloque dedicado, admin-only), y el comportamiento para
                  reservas NO pendientes no cambia. */}
              {user?.role !== 'CLIENT' && !(isAdminRole && selectedBooking.staffAssignmentStatus === 'PENDING') && (
                <div className="space-y-2">
                  <Label>Personal asignado</Label>
                  <Select
                    value={selectedStaffId}
                    onValueChange={setSelectedStaffId}
                  >
                    <SelectTrigger data-testid="booking-staff-select">
                      <SelectValue placeholder="Seleccionar personal" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffUsers
                        .filter(staff => (staff.serviceIds || []).includes(selectedBooking.calendarId))
                        .map((staff) => (
                          <SelectItem key={staff.id} value={staff.id}>
                            {staff.fullName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleUpdateStaff}
                    disabled={formLoading || selectedStaffId === (selectedBooking.staffId || '')}
                    data-testid="update-booking-staff-btn"
                  >
                    Guardar personal
                  </Button>
                </div>
              )}

              {/* No show reason input */}
              {user?.role !== 'CLIENT' && selectedBooking.status !== 'NO_SHOW' && (
                <div className="space-y-2">
                  <Label>{t('bookings.noShowReason')}</Label>
                  <Textarea
                    value={noShowReason}
                    onChange={(e) => setNoShowReason(e.target.value)}
                    placeholder={t('bookings.noShowReason')}
                    className="input-tactical"
                    data-testid="noshow-reason-input"
                  />
                </div>
              )}

              {/* Status buttons */}
              {user?.role !== 'CLIENT' && (
              <div className="flex flex-wrap gap-2">
                {selectedBooking.status !== 'ATTENDED' && (
                  <Button
                    variant="outline"
                    className="flex-1 border-green-600 text-green-400 hover:bg-green-600/10"
                    onClick={() => handleUpdateStatus('ATTENDED')}
                    disabled={formLoading}
                    data-testid="mark-attended-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {t('bookings.markAttended')}
                  </Button>
                )}
                
                {selectedBooking.status !== 'NO_SHOW' && (
                  <Button
                    variant="outline"
                    className="flex-1 border-red-600 text-red-400 hover:bg-red-600/10"
                    onClick={() => handleUpdateStatus('NO_SHOW')}
                    disabled={formLoading}
                    data-testid="mark-noshow-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('bookings.markNoShow')}
                  </Button>
                )}
              </div>
              )}

              {/* Internal note */}
              {user?.role !== 'CLIENT' && (
                <div className="space-y-2 pt-1 border-t border-zinc-800">
                  <Label className="flex items-center gap-1.5 text-zinc-400">
                    <Flag className="w-3.5 h-3.5 text-orange-400" />
                    Nota interna
                  </Label>
                  <Textarea
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Solo visible para el equipo..."
                    className="input-tactical resize-none"
                    rows={2}
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleSaveNote}
                    disabled={noteLoading}
                  >
                    {noteLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Guardar nota
                  </Button>
                </div>
              )}

              {/* Delete button */}
              {(user?.role !== 'CLIENT' || selectedBooking?.status === 'RESERVED') && (
              <Button
                variant="ghost"
                className="w-full text-red-400 hover:text-red-300 hover:bg-red-600/10"
                onClick={handleDeleteBooking}
                disabled={formLoading}
                data-testid="delete-booking-btn"
              >
                {t('common.delete')}
              </Button>
              )}

            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Section 4 (R4/4.4): Create Break Dialog — opened from
          `handleQuickAddBreak`. Pre-selects the cell's (calendarId,
          variantId) when the dialog is reached from a cell with that
          context; the user can edit `calendarId` (service selector) and
          `variantId` (variant selector that only appears when the chosen
          service has `enablePlanningVariants === true` AND ≥2 active
          variants). When the feature is OFF for the chosen service, the
          `variantId` field is omitted from the POST. */}
      <Dialog open={showBreakCreateDialog} onOpenChange={setShowBreakCreateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              Crear descanso
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {breakCreateForm.date
                ? `${breakCreateForm.date} · ${breakCreateForm.startTime} – ${breakCreateForm.endTime}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Servicio</Label>
              <Select
                value={breakCreateForm.calendarId}
                onValueChange={(value) => {
                  // When the user changes the service, clear the variant
                  // (a variant of another service is not meaningful here).
                  setBreakCreateForm((f) => ({ ...f, calendarId: value, variantId: '' }));
                }}
              >
                <SelectTrigger data-testid="break-create-service-select">
                  <SelectValue placeholder="Selecciona servicio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__placeholder__" disabled>Sin servicio específico (global)</SelectItem>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: service.color }} />
                        {service.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const activeService = services.find((s) => s.id === breakCreateForm.calendarId);
              const featureOn = activeService?.enablePlanningVariants === true;
              const activeOnly = ((activeService?.variants) || []).filter((v) => v.active !== false);
              if (!featureOn || activeOnly.length < 2) return null;
              return (
                <div className="space-y-2" data-testid="break-create-variant-select">
                  <Label>Variante</Label>
                  <Select
                    value={breakCreateForm.variantId || '__placeholder__'}
                    onValueChange={(v) => setBreakCreateForm((f) => ({
                      ...f,
                      variantId: v === '__placeholder__' ? '' : v,
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona variante" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__placeholder__">Todas las variantes del servicio</SelectItem>
                      {activeOnly.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowBreakCreateDialog(false)}
                disabled={formLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleCreateBreakSubmit}
                disabled={formLoading || !breakCreateForm.calendarId}
                data-testid="break-create-submit-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear descanso'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Break Dialog */}
      <Dialog open={showBreakDialog} onOpenChange={setShowBreakDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              Eliminar descanso
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {selectedBreak ? `${selectedBreak.startTime} – ${selectedBreak.endTime}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowBreakDialog(false)} disabled={formLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="ghost"
              className="text-red-400 hover:text-red-300 hover:bg-red-600/10"
              onClick={handleDeleteBreak}
              disabled={formLoading}
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Day as Template Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide flex items-center gap-2">
              <Download className="w-4 h-4" />
              Exportar como plantilla
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {format(selectedDate, 'EEEE d MMMM', { locale })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre de la plantilla</Label>
              <Input
                value={exportTemplateName}
                onChange={e => setExportTemplateName(e.target.value)}
                placeholder="Ej: Lunes tipo"
                className="input-tactical"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowExportDialog(false)} disabled={exportLoading}>
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                disabled={!exportTemplateName.trim() || exportLoading}
                onClick={async () => {
                  setExportLoading(true);
                  try {
                    await api.post('/weekly-templates/from-day', {
                      date: format(selectedDate, 'yyyy-MM-dd'),
                      name: exportTemplateName.trim(),
                    });
                    toast.success(`Plantilla "${exportTemplateName.trim()}" guardada`);
                    setShowExportDialog(false);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al guardar la plantilla');
                  } finally {
                    setExportLoading(false);
                  }
                }}
              >
                {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Apply Template Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={(open) => {
        setShowApplyDialog(open);
        if (!open) {
          setApplyResult(null);
          setApplyError(null);
          applyPayloadRef.current = null;
          setApplyValidationError('');
        }
      }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {t('planner.applyTemplate')}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {applyingService?.name} · {format(selectedDate, 'EEEE d MMMM', { locale })}
            </DialogDescription>
          </DialogHeader>

          {applyError && (
            <div className="space-y-2" role="alert" data-testid="planner-apply-error">
              <p className="text-sm text-red-400">{applyError}</p>
              {applyPayloadRef.current && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => submitApply(applyPayloadRef.current)}
                  disabled={applyLoading}
                  data-testid="planner-apply-retry"
                >
                  Reintentar
                </Button>
              )}
            </div>
          )}
          {applyResult ? (
            /* Post-apply result */
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2" data-testid="planner-apply-summary">
                {[
                  ['created', 'Creadas'],
                  ['already_existing', 'Ya existentes'],
                  ['failed', 'Fallidas'],
                ].map(([key, label]) => (
                  <div key={key} className="p-3 bg-zinc-900 border border-zinc-800 rounded-sm text-center">
                    <div className="text-2xl font-bold tabular-nums text-zinc-100" data-testid={`planner-apply-${key}`}>
                      {applyResult.summary?.[key] ?? 0}
                    </div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</div>
                  </div>
                ))}
              </div>
              {applyResult.entries?.filter((entry) => entry && entry.date).length > 0 && (
                <div className="space-y-2" data-testid="planner-apply-entries">
                  {Object.entries(
                    applyResult.entries
                      .filter((entry) => entry && entry.date)
                      .reduce((groups, entry) => {
                        (groups[entry.date] ||= []).push(entry);
                        return groups;
                      }, {})
                  ).map(([date, entries]) => (
                    <div key={date} className="border border-zinc-800 rounded-sm p-2">
                      <div className="text-xs font-semibold text-zinc-300">{date}</div>
                      {entries.map((entry, index) => (
                        <div key={`${date}-${entry.time || index}`} className="text-xs text-zinc-500 flex justify-between gap-2">
                          <span>{entry.time || '—'} · {entry.status || 'unknown'}</span>
                          {entry.reason && <span>{entry.reason}</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              <Button
                className="w-full"
                variant="outline"
                  onClick={() => {
                    setShowApplyDialog(false);
                    setApplyResult(null);
                    setApplyError(null);
                    applyPayloadRef.current = null;
                  }}
              >
                {t('common.close')}
              </Button>
            </div>
          ) : (
            /* Template selector */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="planner-apply-start-date">Desde</Label>
                  <Input
                    id="planner-apply-start-date"
                    aria-label="Desde"
                    type="date"
                    value={applyStartDate}
                    onChange={(event) => {
                      setApplyStartDate(event.target.value);
                      setApplyValidationError('');
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="planner-apply-end-date">Hasta</Label>
                  <Input
                    id="planner-apply-end-date"
                    aria-label="Hasta"
                    type="date"
                    value={applyEndDate}
                    onChange={(event) => {
                      setApplyEndDate(event.target.value);
                      setApplyValidationError('');
                    }}
                  />
                </div>
              </div>
              {applyValidationError && (
                <p className="text-sm text-red-400" role="alert" data-testid="planner-apply-validation-error">
                  {applyValidationError}
                </p>
              )}
              {templates.length === 0 ? (
                <p className="text-sm text-zinc-500 py-2 text-center">
                  {t('planner.noTemplatesHint')}
                </p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setSelectedTemplateId(tpl.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-sm border text-sm transition-colors ${
                        selectedTemplateId === tpl.id
                          ? 'border-zinc-400 bg-zinc-800 text-zinc-100'
                          : 'border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full flex-shrink-0 border ${
                            selectedTemplateId === tpl.id
                              ? 'bg-zinc-100 border-zinc-100'
                              : 'border-zinc-600'
                          }`}
                        />
                        {tpl.name}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setShowApplyDialog(false)}
                  disabled={applyLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  className="btn-primary"
                  disabled={!selectedTemplateId || applyLoading}
                  onClick={async () => {
                    if (applyEndDate < applyStartDate) {
                      setApplyValidationError('La fecha Hasta debe ser igual o posterior a Desde.');
                      return;
                    }
                    submitApply({
                      templateId: selectedTemplateId,
                      payload: {
                        calendarId: applyingService.id,
                        startDate: applyStartDate,
                        endDate: applyEndDate,
                      },
                    });
                  }}
                >
                  {applyLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CalendarCheck className="w-4 h-4 mr-2" />
                      {t('planner.apply')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
